import { plierLaTypographie, type CanvasActionInput, type CanvasActionName, type CanvasDocumentMode, type CanvasImageBytes, type CanvasSelector } from '@hector/shared-types';
import type { ResolveurDEnTeteNomme } from '../canvasEditorStore';
import type { CanvasDocumentWriter } from '../canvasDocumentScope';
import { isOpenParagraphInsertionRevision, type AgentTrackedInsertionLedger } from './agentTrackedInsertionLedger';
import {
  estUnTexteDeRevisionCoupe,
  idsDeRevision,
  readCanvasSnapshot,
  readCompleteTrackedChangesAtDocumentRevision,
  type CanvasSnapshot,
  type CanvasSnapshotBlock,
  type CanvasSnapshotRevision,
} from './canvasSnapshot';
import {
  buildVerifiedReceipt,
  abortedBeforeMutation,
  inconclusiveAfterMutation,
  toCanvasSnapshotSummary,
  type CanvasActionReceipt,
  type ResolvedActionTarget,
} from './actionReceipt';
import { estArretDeLot, type JetonDEpoque, type PeutEtreUnePromesse, type VerificateurDEpoque } from './executionAsynchrone';
import { muterSousFences, REVISION_ABSENTE, revisionCourante, type RevisionDeDocument } from './fencesDeMutation';
import {
  areTargetsInFinalInspection,
  avisDeMarquesMarkdown,
  citerLeTexteCherche,
  isCompletePublicRange,
  isTargetInFinalInspection,
  MAX_ACTIONABLE_SELECTOR_CANDIDATES,
  messageParagrapheDeCelluleNonResolu,
  messageRangeNotInFinalInspection,
  messageSelecteurIntrouvable,
  messageTargetNotInFinalInspection,
  resolveCanvasSelector,
  resoudreUnParagrapheDeCellule,
  type ParagrapheDeCelluleResolu,
} from './selectorResolver';
import { estObjet } from './apercuDePlan';
import { isCanvasProjectionReadError, lireProjectionMarkdown } from './canvasProjection';
import { imagesDuCorps, imagesPorteesParLesBlocs, listerLesImages, type ImageDuCorps } from './imagesDuCorps';
import { estUnRefusDeTexteDansUnChamp, retirerLesChampsVidesDuBloc } from './champsVides';
import { lireLesZonesDePage, pagesPriveesDUnePartie } from './pagesSansLeurPartie';
import type { PeintreDuDocument } from './peintreDeLEditeur';
import { estUnEchecDuFournisseur } from './refusPassager';
import { remplacementPour } from './remplacementPartout';
import type { SuperDocDocumentApi } from './superDocDocumentApi';
import { signalerLAvancee } from './avanceeParLots';

type ProviderMethod = (input?: unknown, options?: unknown) => unknown;
/**
 * Les verbes portés par CE module, nommés POSITIVEMENT (#992).
 *
 * ⚠️ La liste s'écrivait par exclusion (`Exclude<…, 'insert_paragraphs' |
 * 'apply_style' | 'write_markdown'>`), ce qui faisait tomber ici **tout verbe
 * neuf** du catalogue partagé : le `satisfies` du bas de fichier réclamait alors
 * un exécuteur pour un verbe implémenté ailleurs. La forme positive rend
 * l'appartenance à ce module explicite, et `Extract` garantit qu'aucun nom ne
 * peut y être écrit s'il quitte le catalogue.
 */
export type CanvasTextActionName = Extract<
  CanvasActionName,
  'replace_text' | 'delete_block_range' | 'accept_tracked_changes' | 'reject_tracked_changes'
  | 'undo_changes' | 'redo_changes' | 'move_range'
>;
type TextActionInput = Extract<CanvasActionInput, { action: CanvasTextActionName }>;
// `expectedRevision` porte le type MARQUÉ, pas `string` : deux surfaces du
// provider rendent une « revision » et une seule est acceptée par le verrou
// (#980). Voir `RevisionDeDocument` dans `fencesDeMutation.ts`.
type TrackedMutationOptions = { changeMode: 'tracked'; expectedRevision: RevisionDeDocument };
type DirectMutationOptions = {
  changeMode: 'direct';
  expectedRevision: RevisionDeDocument;
  /** Guard returned by SuperDoc's exact capability preflight, when required. */
  supportCheck?: unknown;
};
export type CanvasMutationOptions = TrackedMutationOptions | DirectMutationOptions;

export interface CanvasActionContext {
  doc: SuperDocDocumentApi & {
    replace?: ProviderMethod;
    /**
     * ⚠️ **L'undo vit sous `history`, jamais à la racine** (#987).
     *
     * `doc.undo` n'a jamais existé dans le Document API : `undo_changes`
     * rendait donc `CAPABILITY_UNAVAILABLE` à chaque appel depuis sa mise en
     * service, et aucun test ne rougissait parce que le double de test
     * (`documentApiFactice`) définissait, lui, un `undo` racine. Un faux qui
     * publie ce que le provider n'a pas est aveugle par construction.
     *
     * `capabilities.get()` annonce `history.undo` **disponible** : la panne
     * était une erreur d'ADRESSE, pas une capacité absente.
     *
     * ⚠️ `history.undo()` ne prend **ni input ni options** — le verrou optimiste
     * `expectedRevision` n'a aucun équivalent sur ce chemin. La preuve de
     * l'undo reste donc le mouvement de révision relu avant/après.
     */
    /**
     * `redo` est le pendant exact d'`undo` — ni input ni options (#996) — et
     * `get` publie `{undoDepth, redoDepth, canUndo, canRedo}` : c'est elle qui
     * permet de REFUSER avant d'essayer, plutôt que de faire dépenser un tour à
     * l'agent pour apprendre qu'il n'y a rien à rétablir.
     */
    history?: { undo?: ProviderMethod; redo?: ProviderMethod; get?: ProviderMethod };
    /**
     * ⚠️ `blocks.move` figure dans `V1_RUNTIME_UNAVAILABLE_OPERATION_IDS` des
     * `.d.ts` et **fonctionne** (mesuré : rang 3 → rang 1). Seconde opération de
     * cette liste à démentir sa propre déclaration, après `lists.remove` en
     * #994 : cette constante est PÉRIMÉE, `capabilities.get()` fait autorité.
     */
    blocks?: SuperDocDocumentApi['blocks'] & { delete?: ProviderMethod; move?: ProviderMethod };
    trackChanges?: SuperDocDocumentApi['trackChanges'] & { decide?: ProviderMethod };
    mutations?: { apply?: ProviderMethod };
    query?: { match?: ProviderMethod };
    /**
     * Les notes de bas de page — six opérations, toutes `available` sur
     * `superdoc@2.6.0` (relevé du 2026-08-26, §9 du vault).
     *
     * ⚠️ **`content`, jamais `text`**, et les deux verbes qui visent une note
     * existante prennent une adresse d'ENTITÉ (`{kind:'entity',
     * entityType:'footnote', noteId}`), pas un `noteId` à plat. `insert` exige
     * un `at` EXPLICITE malgré son typage optionnel : l'adaptateur v2 rend
     * `PRECONDITION_FAILED — requires an explicit target address`.
     *
     * ⚠️ `update` se déclare `tracked:true` et ne produit **AUCUNE** révision
     * suivie — quatrième démenti du `tracked` annoncé, contrôlé sur une note
     * commitée. C'est ce qui met `edit_footnote` en `reviewPolicy:'direct'` au
     * catalogue partagé. `insert` et `remove`, eux, en produisent bien une.
     *
     * `get` et `configure` existent aussi et ne sont utilisées par aucun verbe :
     * les déclarer ici les ferait passer pour un chemin vivant.
     */
    footnotes?: {
      list?: ProviderMethod;
      insert?: ProviderMethod;
      update?: ProviderMethod;
      remove?: ProviderMethod;
    };
    /**
     * Les familles de l'EN-TÊTE, pour `set_letterhead` (2026-09-06).
     *
     * Elles sont déclarées ici comme les notes ci-dessus : seulement ce qu'un
     * verbe emprunte réellement. La géométrie, elle, n'est PAS réimplémentée —
     * l'exécuteur délègue à `editor/canvasLetterhead.ts`, qui porte déjà
     * l'ordre load-bearing (lire l'image AVANT de retirer quoi que ce soit),
     * l'orphelinat relu avant suppression, et le refus sur en-tête hérité.
     */
    sections?: {
      list?: ProviderMethod;
      setTitlePage?: ProviderMethod;
    };
    headerFooters?: {
      list?: ProviderMethod;
      resolve?: ProviderMethod;
      parts?: { list?: ProviderMethod; create?: ProviderMethod; delete?: ProviderMethod };
      refs?: { set?: ProviderMethod; clear?: ProviderMethod };
    };
    /**
     * `paragraph` et `table` étaient déjà empruntées (insertion, tables) sans
     * être déclarées ici ; `image` les rejoint pour l'en-tête. Les trois sont
     * nommées ensemble parce qu'un type ne portant qu'`image` rendrait
     * incompatibles tous les doubles de test qui publient `create.paragraph`.
     */
    create?: { paragraph?: ProviderMethod; table?: ProviderMethod; image?: ProviderMethod };
    /** `{nodeId, address, rows, columns}` d'un tableau : `delete_block_range` y lit son nombre de lignes avant de le supprimer. */
    tables?: { get?: ProviderMethod };
  };
  expectedRevision?: RevisionDeDocument;
  /**
   * Provenance broker-owned from the latest native `projection:"final"`.
   * Undefined means a pre-#1605 envelope; an empty Set means a native
   * projection with no publicly targetable block.
   */
  inspectedPublicNodeIds?: ReadonlySet<string>;
  /**
   * One-use broker authority for the physical empty paragraph that Word places
   * in a newly-created DOCX. It lets `write_markdown mode:"replace"` write
   * through the styled write point and then remove that exact paragraph, only
   * while it is still the complete body; it is not a general block target and
   * it disappears on the next inspection or remount.
   */
  initialBlankTemplateParagraphId?: string;
  /**
   * Ephemeral proof that this exact mounted Canvas created an open qualified
   * paragraph insertion. It never identifies a Word author and is absent on reload or
   * remount, where automatic rejection must remain unavailable.
   */
  agentTrackedInsertionLedger?: AgentTrackedInsertionLedger;
  /**
   * Régime de révision DU DOCUMENT au moment où l'action a commencé (#990).
   *
   * Lu une fois par `superDocCanvasExecutor`, jamais relu en cours d'action :
   * un document ne porte qu'un régime, une action non plus. `undefined` vaut
   * « suivi » — le repli le plus exigeant pour un contexte qui ne dit pas son
   * régime ; l'exécuteur réel passe toujours celui du document.
   */
  regimeSuivi?: boolean;
  /**
   * L'état du peintre de l'éditeur de ce document, porté par son scope : les
   * écritures de forme le lisent et le tiennent à jour (`peintreDeLEditeur.ts`).
   * Absent : le contexte n'en porte pas, l'écriture en tient un le temps de l'appel.
   */
  peintre?: PeintreDuDocument;
  /**
   * Le point de passage de **chaque appel mutant** (#939) : il marque la
   * mutation comme tentée et porte le réessai du refus passager. Il ne bascule
   * pas le mode de l'éditeur, qui est celui du régime de suivi (#1838).
   *
   * ⚠️ Aucune LECTURE ne doit passer par ici : une lecture marquée « mutation
   * tentée » ferait rendre `non_verifiable` à un refus qui n'a rien écrit.
   *
   * ⚠️ **`operation` peut être REJOUÉE** (#1860) : si elle rend le refus passager
   * du fournisseur — écriture refusée pendant un export — elle est rappelée
   * après une courte attente (`refusPassager.ts`). C'est sûr tant qu'elle porte
   * UN seul appel fournisseur et rend son reçu tel quel : un refus n'a rien
   * écrit. Une fermeture qui enchaîne plusieurs appels doit rendre un résultat à
   * elle, jamais le reçu brut du dernier — sinon le rejeu réappliquerait les
   * premiers.
   *
   * ⚠️ `options` n'est plus lu par aucune implémentation de production depuis
   * #1838 (il servait à ouvrir la fenêtre de suggestion, supprimée). Il reste au
   * contrat parce que le retirer touche les 36 appels de `muterSousFences`, dans
   * douze fichiers : à faire d'un seul geste, hors de ce ticket.
   */
  mutate: <T>(operation: () => PeutEtreUnePromesse<T>, options: CanvasMutationOptions) => Promise<T>;
  /**
   * LE POINT D'ÉCRITURE DU CANVAS, celui de la génération d'acte (#993).
   *
   * C'est par lui — et par lui seul — que `write_markdown` pose un document :
   * même parseur partagé (`StructuralParser`), même file sérialisée, même
   * attente bornée, même normalisation structurelle que la cascade de
   * génération. Un exécuteur qui poserait les blocs lui-même par `ctx.doc`
   * rouvrirait le second chemin d'écriture que #937 a fermé, et le rendu d'un
   * mail divergerait de celui d'un acte au premier correctif de peintre.
   *
   * ⚠️ Il pose ses blocs et leurs finitions en `changeMode:'direct'`
   * (`editor/modeDEcriture.ts`), quel que soit le mode de l'éditeur. C'est ce qui fait de
   * `write_markdown` l'exception nommée du régime de révision (#990) — voir
   * `reviewPolicy:'direct'` dans le catalogue partagé.
   *
   * `undefined` quand aucun canvas n'est monté (ou son montage en cours) : le
   * verbe refuse alors AVANT toute mutation plutôt que d'écrire dans le vide.
   * Un `undefined` n'est pas un `false` — il dit « je n'ai pas de point
   * d'écriture », pas « l'écriture est impossible ».
   */
  /** Corrélation wire de la requête Canvas, jamais ses arguments. */
  canvasRequestId?: string;
  /**
   * Les octets de l'image qu'`insert_image` pose ou qu'`edit_image` substitue,
   * quand sa source est une pièce du dossier ou un fichier joint (#2141).
   *
   * Ils viennent de l'ENVELOPPE (`imageBytes`), où le backend les a joints
   * après ses gardes d'accès, jamais des arguments de l'action. Absents : le
   * verbe refuse avant toute écriture (`sourceDImage.ts`).
   */
  imageJointe?: CanvasImageBytes;
  /** Factual write-point units only; no selector, block identity, or document content. */
  onChunkCompleted?: (progress: { completedUnits: number; totalUnits: number }) => void;
  ecrireDocument?: CanvasDocumentWriter;
  /**
   * Résout un en-tête de cabinet PAR SON NOM et en rend les octets, pour
   * `set_letterhead` (2026-09-06).
   *
   * ⚠️ **C'est le PREMIER champ du contexte à porter une latence réseau.** Les
   * vingt autres verbes ne font que transformer le document en mémoire ; celui-ci
   * doit d'abord demander au serveur quels en-têtes le cabinet possède, puis
   * télécharger une image. Deux conséquences pour qui écrira le suivant :
   * `ctx.verifierEpoque` doit être ré-asserté APRÈS l'attente (le document peut
   * avoir été remplacé pendant), et un rejet réseau n'est pas un refus métier —
   * « ce cabinet n'a pas cet en-tête » et « on n'a pas pu demander » sont deux
   * accusés différents.
   *
   * `undefined` quand aucun canvas n'est monté : même doctrine
   * qu'`ecrireDocument` — le verbe refuse avant toute mutation.
   */
  resoudreLEnTeteNomme?: ResolveurDEnTeteNomme;
  /**
   * Règle le régime de suivi du document — celui que le bouton de la barre
   * affiche — et rend le mode RELU sur l'éditeur après le réglage (#1838).
   *
   * Seul `set_review_mode` l'appelle. Ce n'est pas une mutation du document :
   * il ne passe pas par `mutate`.
   *
   * `undefined` quand aucun canvas n'est monté : le verbe refuse alors, même
   * doctrine qu'`ecrireDocument`.
   */
  reglerLeSuivi?: (suiviActif: boolean) => CanvasDocumentMode;
  /**
   * Ce que l'avocat VOIT de ce document (#1586), lu par `write_markdown
   * replace` avant d'effacer quoi que ce soit.
   *
   * `hoteParque` est paresseuse : elle lève hors navigateur. ⚠️ `undefined`, ou
   * une lecture qui lève, vaut « l'avocat ne le voit pas » — l'inverse de la
   * doctrine des capacités (#987), et c'est délibéré : ici l'ignorance couvre un
   * remplacement direct, sans révision, que l'autosave persiste.
   */
  visibiliteDuDocument?: {
    readonly montage: JetonDEpoque;
    readonly hoteParque: () => boolean;
  };
  /**
   * Ré-assertion de l'époque, appelée avant CHAQUE appel mutant, avant
   * l'émission du receipt et avant le rafraîchissement de révision (#939).
   * Jette `EpoqueDepasseeError` quand le document a changé sous l'action.
   */
  verifierEpoque: VerificateurDEpoque;
}

export type CanvasActionExecutor = (ctx: CanvasActionContext, input: never) => Promise<CanvasActionReceipt>;
const ACTION_SCAN_PAGE_SIZE = 250;

export function snapshot(ctx: CanvasActionContext, window = { offset: 0, limit: 80 }): Promise<CanvasSnapshot> {
  return readCanvasSnapshot(ctx.doc, window);
}

type ReviewSafeSnapshot =
  | { ok: true; snapshot: CanvasSnapshot }
  | { ok: false; snapshot: CanvasSnapshot; reason: string };

/**
 * The model-facing snapshot is intentionally bounded and best-effort. A review
 * mutation needs the opposite contract: every story/page must be known and the
 * document must still be on the revision whose blocks we resolved. This helper
 * remains local to the two review-sensitive text actions so regular editing
 * does not pay a full tracked-change scan.
 */
async function snapshotWithCompleteTrackedChanges(
  ctx: CanvasActionContext,
  window = { offset: 0, limit: 80 },
): Promise<ReviewSafeSnapshot> {
  const initial = await snapshot(ctx, window);
  if (initial.revision === REVISION_ABSENTE) {
    return { ok: false, snapshot: initial, reason: 'editor.doc.info() did not expose the document revision needed to bind the review state to the resolved blocks' };
  }
  const complete = await readCompleteTrackedChangesAtDocumentRevision(ctx.doc, initial.revision);
  if (!complete.ok) return { ok: false, snapshot: initial, reason: complete.reason };
  return {
    ok: true,
    snapshot: {
      ...initial,
      counts: { ...initial.counts, trackedChanges: complete.trackedChanges.length },
      trackedChanges: complete.trackedChanges,
    },
  };
}

function unverifiedTrackedChangesMessage(intent: CanvasActionName, reason: string): string {
  return `${intent} refused before mutation because the complete tracked-change state could not be proven: ${reason}. A missing or paginated review entry is never evidence that no paragraph wrapper exists. Nothing was modified; re-run canvas_inspect after the editor review state is available.`;
}

/** Keeps model-facing review diagnostics actionable without exposing an unbounded provider payload. */
function describeTrackedChangeIds(ids: readonly string[]): string {
  const visible = ids.slice(0, 8).map((id) => JSON.stringify(id.length > 160 ? `${id.slice(0, 160)}…` : id));
  const remaining = ids.length - visible.length;
  return `${ids.length} id(s): ${visible.join(', ')}${remaining > 0 ? `; ${remaining} additional id(s) omitted` : ''}`;
}

/** Extracts the only provider failure details that are safe to surface in a receipt. */
function describeTrackedChangeProviderFailure(provider: unknown): string {
  if (typeof provider !== 'object' || provider === null) {
    return 'the provider returned no structured failure details';
  }
  const failure = (provider as { failure?: unknown }).failure;
  if (typeof failure !== 'object' || failure === null) {
    return 'the provider returned success:false without a structured failure code or message';
  }
  const code = (failure as { code?: unknown }).code;
  const message = (failure as { message?: unknown }).message;
  const codeDescription = typeof code === 'string' && code.length > 0 ? `code=${code.slice(0, 120)}` : undefined;
  const messageDescription = typeof message === 'string' && message.length > 0
    ? `message=${JSON.stringify(message.slice(0, 200))}`
    : undefined;
  return [codeDescription, messageDescription].filter((part): part is string => part !== undefined).join(', ')
    || 'the provider returned success:false without a string failure code or message';
}

export async function scanAllBlocks(ctx: CanvasActionContext): Promise<CanvasSnapshotBlock[]> {
  const blocks: CanvasSnapshotBlock[] = [];
  for (let offset = 0; ; offset += ACTION_SCAN_PAGE_SIZE) {
    const page = await snapshot(ctx, { offset, limit: ACTION_SCAN_PAGE_SIZE });
    blocks.push(...page.blocks);
    if (page.rawBlocksCount < ACTION_SCAN_PAGE_SIZE) return blocks;
  }
}

export function targetSummary(target: CanvasSnapshotBlock, candidateCount?: number): ResolvedActionTarget {
  return { nodeId: target.nodeId, ordinal: target.ordinal, text: target.text.slice(0, 500), ...(candidateCount === undefined ? {} : { candidateCount }) };
}

/**
 * Turns a closed ambiguity refusal into the next usable model action.
 *
 * The resolver deliberately exposes only its first eight candidates: returning
 * every duplicate could be larger than the document window, while the agent can
 * retrieve the full paginated set with `canvas_inspect({selector})` before it
 * chooses a nodeId, narrower terms, or an explicit occurrence. No rank is ever
 * shown: the editor publishes none since #1605, and a rank printed here taught
 * the model a selector that is always refused.
 */
function messageSelecteurAmbigu(
  candidateCount: number,
  candidates: ReadonlyArray<Pick<CanvasSnapshotBlock, 'nodeId' | 'text'>>,
): string {
  const preview = (text: string): string => {
    const compact = text.replace(/\s+/gu, ' ').trim();
    return compact.length > 160 ? `${compact.slice(0, 160)}…` : compact;
  };
  const listed = candidates
    .map((candidate) => `nodeId=${candidate.nodeId}, preview=${JSON.stringify(preview(candidate.text))}`)
    .join('; ');
  return `canvas_perform_action textSearch matched ${String(candidateCount)} candidates, so no destructive mutation was attempted. `
    + `Candidate previews (first ${String(candidates.length)} in document order): ${listed}. `
    + 'Nothing was written. Next step: resend the action with the nodeId of the intended candidate above, narrower terms, or occurrence (1-based, document order); call canvas_inspect with the same selector only if the candidate you want is not listed.';
}

/** Explains why a bypassed schema input must be rejected before document mutation. */
function messageTermeSelecteurInvalide(): string {
  return 'canvas_perform_action refused the textSearch selector before mutating the document: at least one term is empty after French typography normalization (whitespace, non-breaking space, BOM or zero-width characters). Next step: provide a visible word or phrase copied from canvas_inspect; occurrence cannot make an empty term target a block.';
}

async function resolveTarget(ctx: CanvasActionContext, selector: CanvasSelector): Promise<{ ok: true; target: CanvasSnapshotBlock; candidateCount: number } | { ok: false; code: string; message: string }> {
  const result = await resolveCanvasSelector(ctx.doc, selector, {
    requireUnique: true,
    inspectedPublicNodeIds: ctx.inspectedPublicNodeIds,
  });
  if (!result.ok) {
    return {
      ok: false,
      code: result.code,
      message: result.code === 'AMBIGUOUS_SELECTOR'
        ? messageSelecteurAmbigu(result.candidateCount, result.candidates)
        : result.code === 'INVALID_SELECTOR_TERM'
          ? messageTermeSelecteurInvalide()
          : result.code === 'TARGET_NOT_IN_FINAL_INSPECTION'
            ? messageTargetNotInFinalInspection()
            : messageSelecteurIntrouvable('canvas_perform_action', selector, result.candidateCount),
    };
  }
  return result;
}

function refFromUnknownHandle(input: unknown): string | undefined {
  if (typeof input === 'string' && input.trim().length > 0) return input;
  if (typeof input !== 'object' || input === null) return undefined;
  const record = input as { ref?: unknown; handle?: unknown };
  if (typeof record.ref === 'string' && record.ref.trim().length > 0) return record.ref;
  return typeof record.handle === 'object' && record.handle !== null && typeof (record.handle as { ref?: unknown }).ref === 'string'
    ? (record.handle as { ref: string }).ref
    : undefined;
}

function extractMatchItems(input: unknown): Record<string, unknown>[] {
  if (typeof input !== 'object' || input === null) return [];
  const record = input as { items?: unknown; matches?: unknown };
  const items = Array.isArray(record.items) ? record.items : Array.isArray(record.matches) ? record.matches : [];
  return items.filter((item): item is Record<string, unknown> => typeof item === 'object' && item !== null);
}

async function resolveTextRef(
  ctx: CanvasActionContext,
  target: Pick<CanvasSnapshotBlock, 'nodeId' | 'nodeType'>,
  pattern: string,
): Promise<{ ok: true; ref: string } | { ok: false; code: string; message: string }> {
  if (typeof ctx.doc.query?.match !== 'function') {
    return { ok: false, code: 'CAPABILITY_UNAVAILABLE', message: 'canvas_perform_action requires editor.doc.query.match to derive a writable text ref inside the resolved block (used by replace_text).' };
  }
  const result = await ctx.doc.query.match({
    type: 'text',
    pattern,
    require: 'unique',
    within: { kind: 'block', nodeType: target.nodeType, nodeId: target.nodeId },
  });
  const items = extractMatchItems(result);
  // ⚠️ Ces trois refus sont rendus À UN MODÈLE, hors contexte (#991). Ils
  // nomment donc le bloc, la portion de texte cherchée et LE GESTE SUIVANT —
  // « ré-inspecte » n'en est pas un quand la ré-inspection rendra exactement le
  // même document : c'est le cycle qui a coûté six échecs et trois minutes dans
  // la conversation autopsiée avant que l'agent ne se résolve à purger le
  // document de l'avocat.
  const apercu = pattern.length > 80 ? `${pattern.slice(0, 80)}…` : pattern;
  if (items.length === 0) {
    return { ok: false, code: 'SELECTOR_NOT_FOUND', message: `The resolved block (nodeId=${target.nodeId}) does not contain the text that must be matched to obtain a writable ref: "${apercu}". The block text has changed since it was read, or an invisible difference (non-breaking space, tracked deletion, soft line break) prevents the match. Next step: re-run canvas_inspect to read the block's CURRENT text, then retry with that exact text — or use insert_paragraphs to add new content instead of rewriting this block.` };
  }
  if (items.length !== 1) {
    return { ok: false, code: 'AMBIGUOUS_SELECTOR', message: `The resolved block (nodeId=${target.nodeId}) contains ${items.length} writable matches for "${apercu}", so the edit has no single target. Next step: narrow the edit to a longer, unique portion of the sentence rather than a repeated fragment.` };
  }
  const ref = refFromUnknownHandle(items[0]);
  return ref === undefined
    ? { ok: false, code: 'SELECTOR_REF_UNAVAILABLE', message: `The text match inside the block (nodeId=${target.nodeId}) carries no writable ref, so nothing can be written there. This is a provider-side limitation on this block, not a targeting mistake: retrying the same call will fail identically. Next step: use insert_paragraphs to add the content next to this block, or delete_block_range plus insert_paragraphs to replace it.` }
    : { ok: true, ref };
}

async function summaryForFailure(ctx: CanvasActionContext, intent: CanvasActionName, code: string, message: string): Promise<CanvasActionReceipt> {
  return abortedBeforeMutation({ intent, snapshot: toCanvasSnapshotSummary(await snapshot(ctx)), code, message });
}

/**
 * UN appel mutant sous les trois gardes de `fencesDeMutation` (#939), dans le
 * RÉGIME DU DOCUMENT (#990).
 *
 * ⚠️ Le mode n'est plus écrit en dur ici. Le suivi des modifications est une
 * propriété du scope documentaire réglée par l'avocat, pas une
 * propriété du verbe : un document mi-suivi mi-direct est exactement le
 * « mi-rouge mi-noir » que ce ticket ferme, et il conduisait l'agent à
 * conseiller d'accepter dans Word des révisions qui n'existaient pas.
 *
 * Le régime est porté par le contexte, donc **lu une seule fois à l'entrée de
 * l'action** : une bascule de l'avocat pendant un lot ne coupe pas ce lot en
 * deux. Son absence vaut « suivi » : un contexte qui ne dit pas son régime prend
 * le plus exigeant ; l'exécuteur réel passe toujours celui du document.
 */
function trackedMutation<T>(ctx: CanvasActionContext, revision: RevisionDeDocument, etape: string, operation: () => PeutEtreUnePromesse<T>): Promise<T> {
  return muterSousFences(ctx, etape, { changeMode: modeDeRevision(ctx), expectedRevision: ctx.expectedRevision ?? revision }, operation);
}

/**
 * Le mode à passer au PROVIDER, dérivé du régime du document (#990).
 *
 * ⚠️ Deux mécanismes concourent et il faut les deux : le `changeMode` par appel
 * **et** le mode `suggesting` de l'éditeur, que le scope documentaire pose quand
 * le régime est activé. Sous `editing`, une opération demandée en `tracked` est
 * acceptée et ne produit rien (mesuré). Ils doivent donc dire la même chose : un
 * `changeMode:'tracked'` écrit en dur dans l'appel provider pendant que
 * l'éditeur est en `editing` demanderait un suivi que rien ne peut produire — et
 * le receipt le déclarerait « écrit sans suivi » alors que c'est l'avocat qui a
 * coupé le suivi.
 */
export function modeDeRevision(ctx: CanvasActionContext): 'tracked' | 'direct' {
  return ctx.regimeSuivi === false ? 'direct' : 'tracked';
}

function expectedRevisionFor(ctx: CanvasActionContext, revision: RevisionDeDocument): RevisionDeDocument {
  return ctx.expectedRevision ?? revision;
}

function compterOccurrences(texte: string, motif: string): number {
  if (motif.length === 0) return 0;
  let occurrences = 0;
  for (let position = texte.indexOf(motif); position !== -1; position = texte.indexOf(motif, position + motif.length)) {
    occurrences += 1;
  }
  return occurrences;
}

/**
 * Le remplacement est-il PROUVÉ par la relecture du bloc ?
 *
 * WHY compter au lieu d'exiger « le texte d'origine a disparu » : quand le
 * remplacement CONTIENT le texte cherché — « article 1240 » → « article 1240
 * du Code civil », cas courant en rédaction juridique — l'ancienne règle
 * échouait toujours. Le banc réel du 2026-09-17 l'a montré sur chaque
 * `replace_text` : révision suivie bien créée, texte bien remplacé, receipt
 * `failed`, et l'agent contraint de relire le document après chaque retouche.
 * La règle reste aussi stricte qu'avant contre un remplacement non appliqué :
 * le bloc doit porter le remplacement, différer du texte relu avant l'action,
 * ET avoir une occurrence de moins du texte cherché, à part celles que le
 * remplacement apporte lui-même. Sans le changement de texte, un no-op pouvait
 * paraître réussi si le remplacement était déjà présent dans le bloc. Le texte
 * supprimé en mode suivi n'apparaît pas dans `fullText` (relevé le même jour).
 */
function remplacementRelu(input: {
  avant: string;
  apres: string | undefined;
  cherche: string;
  remplace: string;
  compare: (value: string) => string;
}): boolean {
  if (input.apres === undefined) return false;
  const avant = input.compare(input.avant);
  const apres = input.compare(input.apres);
  const cherche = input.compare(input.cherche);
  const remplace = input.compare(input.remplace);
  const attendues = compterOccurrences(avant, cherche) - 1 + compterOccurrences(remplace, cherche);
  // Le changement effectif doit se mesurer sur le texte brut : en recherche
  // insensible à la casse, « BALZAC » → « Balzac » est une mutation réelle
  // que `compare` rendrait artificiellement identique. `compare` reste en
  // revanche nécessaire pour vérifier les occurrences selon le contrat de
  // recherche demandé par l'agent.
  return input.apres !== input.avant && apres.includes(remplace) && compterOccurrences(apres, cherche) <= attendues;
}

/** Un couple de `replace_text` résolu contre les blocs où il est cherché. */
interface PassageARemplacer {
  edit: { find: string; replace: string };
  /** Les blocs qui portent le passage. */
  target: CanvasSnapshotBlock[];
  /** Le texte que l'éditeur doit chercher : celui de l'agent, ou celui du DOCUMENT quand seule la typographie les sépare. */
  cherche: string;
  /** Le texte à écrire : `replace`, avec les apostrophes et les espaces du document quand le passage a été trouvé plié. */
  remplace: string;
  /** Vrai quand le passage n'a été trouvé qu'à la typographie près. */
  plie: boolean;
  /** Dans le bloc trouvé plié, le nombre de passages qui se lisent comme `find` ; 0 hors du repli. */
  occurrencesPliees: number;
}

/**
 * Les blocs qui portent `find` : à la lettre d'abord, puis à la typographie près quand aucun ne le porte.
 *
 * WHY le repli : le modèle recopie « l'article 700 » et « 1 500 € » avec une apostrophe droite et des espaces simples
 * quand l'acte porte une apostrophe courbe et des insécables. Le mode à un passage ne trouvait alors aucun bloc
 * (conversation de production du 2026-10-13). Le pliage est celui du mode partout
 * ({@link plierLaTypographie}), un caractère pour un : la position trouvée sur le texte plié vaut sur le texte du
 * document, et c'est CE texte que l'éditeur cherche, lui qui compare à la lettre.
 *
 * Le repli ne joue que si la comparaison garde les longueurs (une mise en minuscules peut en changer une) : sinon la
 * tranche du document ne serait pas sûre, et le passage reste introuvable.
 */
function passageARemplacer(
  searchable: readonly CanvasSnapshotBlock[],
  edit: PassageARemplacer['edit'],
  compare: (value: string) => string,
): PassageARemplacer {
  const exacts = searchable.filter((block) => compare(block.fullText).includes(compare(edit.find)));
  const aLaLettre = { edit, cherche: edit.find, remplace: edit.replace, plie: false, occurrencesPliees: 0 };
  if (exacts.length > 0) return { ...aLaLettre, target: exacts };
  const motif = compare(plierLaTypographie(edit.find));
  const plies = searchable.flatMap((block) => {
    const texte = compare(plierLaTypographie(block.fullText));
    return motif.length === edit.find.length && texte.length === block.fullText.length && texte.includes(motif) ? [{ block, texte }] : [];
  });
  if (plies.length !== 1) return { ...aLaLettre, target: plies.map(({ block }) => block) };
  const { block, texte } = plies[0]!;
  const debut = texte.indexOf(motif);
  const duDocument = block.fullText.slice(debut, debut + motif.length);
  return { edit, target: [block], cherche: duDocument, remplace: remplacementPour(duDocument, edit.replace), plie: true, occurrencesPliees: compterOccurrences(texte, motif) };
}

/**
 * Le refus d'un `find` qu'aucun bloc ne porte : ce qui était cherché, où, et la lecture qui le retrouve.
 *
 * WHY : « Each replacement must resolve to exactly one normalized block » ne disait ni le texte, ni le bloc, ni la
 * suite (conversation de production du 2026-10-13).
 */
function messagePassageIntrouvable(find: string, scopeNodeId: string | undefined, sensibleALaCasse: boolean): string {
  const ou = scopeNodeId === undefined ? 'no block of the document contains' : `the scope block nodeId=${scopeNodeId} does not contain`;
  return `replace_text was refused before any write: ${ou} ${citerLeTexteCherche(find)}${sensibleALaCasse ? ' (case-sensitive)' : ''}, even with curly apostrophes and non-breaking spaces folded. Nothing was written.`
    + `${avisDeMarquesMarkdown([find])} A find lies inside ONE block: a passage that runs over two paragraphs is never found. `
    + 'Next step: canvas_inspect with selector:{kind:"textSearch",terms:["<one short distinctive word of the passage>"],match:"all"}, then resend replace_text with find copied from the text it shows'
    + `${scopeNodeId === undefined ? ' and scope:{kind:"nodeId",nodeId:"<that block>"}' : ''}.`;
}

/** Une révision `replacement` ouverte et ancrée sur ce bloc : ce que laisse `rewrite_block`, ou un `replace_text` précédent. */
function estUnRemplacementOuvert(revision: CanvasSnapshotRevision, nodeId: string): boolean {
  return revision.state === 'open' && revision.blockId === nodeId && revision.type === 'replacement';
}

/**
 * Les révisions encore ouvertes sur le bloc avant ET après l'écriture, dans lesquelles elle s'est fondue.
 *
 * WHY : remplacer un passage d'un texte encore en révision ne crée aucune identité, la retouche vit dans la révision
 * en attente. Le reçu disait `TRACKING_NOT_APPLIED` (« écrit sans révision, non révisable ») d'un texte que l'avocat
 * voit en révision (conversation de production du 2026-10-13). Deux cas sont établis, tout autre laisse le reçu juger
 * comme avant :
 * - une insertion de paragraphe ouverte sur le bloc ;
 * - une révision `replacement` ouverte sur le bloc dont le texte inséré a CHANGÉ entre les deux lectures — mesuré le
 *   2026-10-13 sur le paragraphe que `rewrite_block` vient de réécrire sous suivi : même identité, `insertedText` mis
 *   à jour, « tout rejeter » rend le texte d'origine (`tests/ws-mocked/mesure-recus-vrais.spec.ts`, D2). Un texte
 *   inséré inchangé ne prouve rien : l'écriture est alors hors de cette révision. Un texte coupé à la borne de
 *   lecture ne dit pas s'il a changé au-delà : il est tenu pour absorbant, comme l'insertion.
 */
function revisionsOuvertesAbsorbantes(pre: CanvasSnapshot, post: CanvasSnapshot, nodeId: string): string[] {
  const relues = new Map(post.trackedChanges.map((revision) => [revision.id, revision] as const));
  return pre.trackedChanges.filter((avant) => {
    const apres = relues.get(avant.id);
    if (apres === undefined) return false;
    if (isOpenParagraphInsertionRevision(avant, nodeId) && isOpenParagraphInsertionRevision(apres, nodeId)) return true;
    return estUnRemplacementOuvert(avant, nodeId) && estUnRemplacementOuvert(apres, nodeId)
      && (avant.insertedText !== apres.insertedText || estUnTexteDeRevisionCoupe(apres.insertedText));
  }).map((revision) => revision.id);
}

export async function executeReplaceText(ctx: CanvasActionContext, input: Extract<TextActionInput, { action: 'replace_text' }>): Promise<CanvasActionReceipt> {
  if (input.edits.length > 1) {
    // The former `plan:[{kind:"replace",ref,text}]` payload was neither the
    // current SuperDoc mutation-plan contract nor proven in Chromium. Failing
    // closed here prevents a model from paying to build a batch that the
    // provider only rejects after the call. Linked changes now have the native
    // `apply_edit_plan` route, with preview, a revision fence and one receipt.
    // The shared contract already refuses several edits without `everywhere:true`,
    // and the registry routes `everywhere:true` to its own executor: only a
    // forged envelope reaches this line.
    return summaryForFailure(ctx, input.action, 'ATOMIC_BATCH_REQUIRES_EDIT_PLAN', `replace_text without everywhere:true accepts one independent replacement only (got ${input.edits.length} edits). To replace the same texts in the whole document, add everywhere:true and an expectedCount on each edit; for two or more linked retouches in chosen blocks, use apply_edit_plan with stable step ids so native preview validates the whole batch before its single atomic apply. Nothing was modified.`);
  }
  const scope = input.scope === undefined ? undefined : await resolveTarget(ctx, input.scope);
  // Un `scope` que la liste des blocs ne porte pas peut être un paragraphe de CELLULE, que la lecture publie : la
  // recherche y est alors bornée par l'éditeur, et son tableau sert à la relecture (`resoudreUnParagrapheDeCellule`).
  let cellule: Extract<ParagrapheDeCelluleResolu, { ok: true }> | undefined;
  if (scope !== undefined && !scope.ok) {
    const nodeId = scope.code === 'SELECTOR_NOT_FOUND' && input.scope?.kind === 'nodeId' ? input.scope.nodeId : undefined;
    const { find } = input.edits[0]!;
    const resolue = nodeId === undefined ? undefined : await resoudreUnParagrapheDeCellule(ctx.doc, nodeId, find, ctx.inspectedPublicNodeIds);
    if (nodeId === undefined || resolue === undefined) return summaryForFailure(ctx, input.action, scope.code, scope.message);
    if (!resolue.ok) {
      return summaryForFailure(ctx, input.action, scope.code, resolue.raison === 'hors_lecture'
        ? scope.message
        : messageParagrapheDeCelluleNonResolu('replace_text', nodeId, resolue.raison === 'hors_cellule' ? 'hors_cellule' : { cherche: find, raison: resolue.raison }));
    }
    cellule = resolue;
  }
  const searchable = cellule !== undefined ? [cellule.tableau] : scope?.ok ? [scope.target] : await scanAllBlocks(ctx);
  const compare = (value: string) => input.caseSensitive === true ? value : value.toLocaleLowerCase('fr-FR');
  // Dans une cellule, l'éditeur a déjà trouvé `find` tel quel dans le paragraphe visé : le texte du tableau, toutes
  // cellules collées, ne sert pas à le chercher.
  const resolved = cellule !== undefined
    ? input.edits.map((edit) => ({ edit, target: [cellule.tableau], cherche: edit.find, remplace: edit.replace, plie: false, occurrencesPliees: 0 }))
    : input.edits.map((edit) => passageARemplacer(searchable, edit, compare));
  const pliesEnDouble = resolved.find(({ occurrencesPliees, target }) => occurrencesPliees > 1 && target.length === 1);
  if (pliesEnDouble !== undefined) {
    return summaryForFailure(ctx, input.action, 'AMBIGUOUS_SELECTOR',
      `replace_text was refused before any write: ${citerLeTexteCherche(pliesEnDouble.edit.find)} is not in the document as typed, and block nodeId=${pliesEnDouble.target[0]!.nodeId} contains ${pliesEnDouble.occurrencesPliees} passages that read like it once curly apostrophes and non-breaking spaces are folded. Nothing was written. `
      + 'Next step: resend replace_text with a longer find, widened with the words around the passage until it designates one place of that block.');
  }
  const invalidTarget = resolved.find(({ target }) => target.length !== 1);
  if (invalidTarget !== undefined) return summaryForFailure(
    ctx,
    input.action,
    invalidTarget.target.length > 1 ? 'AMBIGUOUS_SELECTOR' : 'SELECTOR_NOT_FOUND',
    invalidTarget.target.length > 1
      ? messageSelecteurAmbigu(invalidTarget.target.length, invalidTarget.target.slice(0, MAX_ACTIONABLE_SELECTOR_CANDIDATES))
      : messagePassageIntrouvable(invalidTarget.edit.find, scope?.ok ? scope.target.nodeId : undefined, input.caseSensitive === true),
  );
  // An unscoped replacement scans raw blocks. Preserve its raw ambiguity
  // calculation, then fence the actual sole target: pre-filtering would shift
  // an occurrence onto a different public block.
  // Le paragraphe de cellule a été reconnu publié par sa résolution : c'est lui que l'appel vise, pas son tableau.
  if (cellule === undefined && resolved.some(({ target }) => target.length === 1 && !isTargetInFinalInspection(target[0]!, ctx.inspectedPublicNodeIds))) {
    return summaryForFailure(ctx, input.action, 'TARGET_NOT_IN_FINAL_INSPECTION', messageTargetNotInFinalInspection());
  }
  const targets = resolved.map(({ target }) => target[0]!);
  /** Le bloc où l'éditeur cherche et écrit, et où il ancre la révision : le paragraphe de cellule, ou le bloc résolu. */
  const lieu = cellule?.adresse ?? targets[0]!;
  const offset = Math.min(...targets.map((target) => target.ordinal)) - 1;
  const pre = await snapshot(ctx, { offset, limit: Math.max(...targets.map((target) => target.ordinal)) - offset });
  const revisionsAvant = idsDeRevision(pre.trackedChanges);
  if (typeof ctx.doc.replace !== 'function') return abortedBeforeMutation({ intent: input.action, snapshot: toCanvasSnapshotSummary(pre), code: 'CAPABILITY_UNAVAILABLE', message: 'replace_text requires editor.doc.replace.' });
  const textRefs: Array<Awaited<ReturnType<typeof resolveTextRef>>> = [];
  for (const { cherche } of resolved) textRefs.push(await resolveTextRef(ctx, lieu, cherche));
  const unresolvedRef = textRefs.find((result) => !result.ok);
  if (unresolvedRef !== undefined && !unresolvedRef.ok) return abortedBeforeMutation({ intent: input.action, snapshot: toCanvasSnapshotSummary(pre), code: unresolvedRef.code, message: unresolvedRef.message });
  const refs = textRefs.map((result) => result.ok ? result.ref : undefined);
  const expectedRevision = expectedRevisionFor(ctx, pre.revision);
  const { cherche, remplace, plie } = resolved[0]!;
  let provider = await trackedMutation(ctx, pre.revision, 'replace_text:replace', () => ctx.doc.replace?.({ ref: refs[0], text: remplace }, { changeMode: modeDeRevision(ctx), expectedRevision }));
  // Le texte cherché traverse un champ Word vide (`champsVides.ts`) : rien n'a été écrit. Les champs vides du bloc
  // sont retirés et le remplacement rejoué UNE fois, sur un ref et une révision relus. Un remplacement qui ne
  // traverse aucun champ ne passe jamais ici : son bloc garde ses champs.
  let champsRetires = 0;
  if (estUnRefusDeTexteDansUnChamp(provider)) {
    champsRetires = await retirerLesChampsVidesDuBloc(ctx, lieu.nodeId, expectedRevision);
    const refRelu = champsRetires > 0 ? await resolveTextRef(ctx, lieu, cherche) : undefined;
    if (refRelu?.ok === true) {
      const options = { changeMode: modeDeRevision(ctx), expectedRevision: await revisionCourante(ctx, pre.revision) };
      provider = await muterSousFences(ctx, 'replace_text:replace-apres-champs-vides', options, () => ctx.doc.replace?.({ ref: refRelu.ref, text: remplace }, options));
    }
  }
  const post = await snapshot(ctx, { offset, limit: pre.blocks.length || 1 });
  // En direct, l'écriture ne vit dans aucune révision, même si celle du bloc a bougé : rien n'y est dit absorbé.
  const absorbantes = modeDeRevision(ctx) === 'tracked' ? revisionsOuvertesAbsorbantes(pre, post, lieu.nodeId) : [];
  const absorbee = absorbantes.length > 0 && idsDeRevision(post.trackedChanges).every((id) => revisionsAvant.includes(id));
  ctx.verifierEpoque('replace_text:receipt');
  return buildVerifiedReceipt({
    intent: input.action,
    regimeSuivi: ctx.regimeSuivi,
    preSnapshot: toCanvasSnapshotSummary(pre),
    postSnapshot: toCanvasSnapshotSummary(post),
    // Lambda explicite, jamais `targets.map(targetSummary)` : `Array.map` passe
    // l'INDEX en second argument, qui tombait dans `candidateCount`. La première
    // cible partait donc avec `candidateCount: 0`, refusé par le contrat
    // (`positive`), et CHAQUE replace_text réussi revenait à l'agent en
    // CANVAS_ACTION_RECEIPT_INVALID (banc réel staging, 2026-09-17, #1571).
    resolvedTargets: targets.map((target) => targetSummary(target)),
    provider,
    revisionsAvant,
    revisionsApres: idsDeRevision(post.trackedChanges),
    revisionsAbsorbantes: absorbantes,
    verification: [
      {
        name: 'replacement_text',
        passed: resolved.every((passage, index) => remplacementRelu({
          avant: targets[index]!.fullText,
          apres: post.blocks.find((block) => block.nodeId === targets[index]!.nodeId)?.fullText,
          cherche: passage.cherche,
          remplace: passage.remplace,
          compare,
        })),
        message: 'Each resolved block must have changed, contain its replacement, and have one occurrence of the original text fewer than before (occurrences carried by the replacement itself excepted).',
      },
    ],
    effects: [{
      kind: 'replace_text',
      reviewability: 'tracked',
      description: (champsRetires > 0
        ? `Replaced the requested text edits, after removing ${champsRetires} empty Word field(s) that the searched text spanned (nothing the lawyer could see). Each removal is one more undo entry before the replacement.`
        : 'Replaced the requested text edits.')
        + (plie ? ` The find was matched with typography folded: the document writes it ${citerLeTexteCherche(cherche)}, and the replacement was written as ${citerLeTexteCherche(remplace)}, with the document's own apostrophes and spaces where yours were plain.` : '')
        + (cellule === undefined ? '' : ` The scope nodeId=${cellule.adresse.nodeId} is a table cell paragraph of table ${cellule.tableau.nodeId}: the text was searched and written inside that paragraph only.`)
        + (absorbee ? ` No new tracked change was created: the new text went into the tracked change already pending on this block (${absorbantes.slice(0, 8).join(', ')}), and the lawyer reviews it there.` : ''),
      nodeIds: targets.map((target) => target.nodeId),
    }],
  });
}

/** Ce que la relecture établit de la suppression d'un bloc. */
type PreuveDeSuppression =
  | { nodeId: string; issue: 'retire_ou_vide' | 'non_prouve' }
  | { nodeId: string; issue: 'tableau_marque'; revisions: string[] };

/**
 * La relecture prouve-t-elle que ce bloc est supprimé ?
 *
 * Un bloc supprimé sous suivi RESTE listé, son texte visible vidé : « absent de la liste » seul serait un faux négatif.
 *
 * ⚠️ Un TABLEAU supprimé sous suivi garde son texte : ses lignes sont marquées supprimées et restent dans le
 * document jusqu'à l'acceptation, comme la ligne qu'`edit_table` supprime (`tableStructureExecutor.ts`,
 * `suppressionProuvee`). Le 2026-10-13, en production, le reçu disait `failed` d'un tableau marqué supprimé par une
 * révision `structural-table-row-deletion`. La preuve est une révision de suppression NEUVE et ancrée sur ce
 * tableau ; elle ne vaut pour aucun autre type de bloc, dont le texte se vide.
 *
 * ⚠️ L'ancrage, mesuré sur l'éditeur réel le 2026-10-13 (`tests/ws-mocked/canvas-recus-vrais-tableau-supprime.spec.ts`,
 * trois tableaux dont un à fusions et un qui en contient un autre) : UNE révision `structural`, de sous-type
 * `table-deletion` (`table-row-deletion` pour un tableau d'une seule ligne), dont `navigationTarget.blockId` est le
 * PREMIER PARAGRAPHE du tableau (`role: "structural-carrier"`), jamais le nodeId du tableau. Le lien entre les deux est l'identifiant que l'éditeur donne au tableau,
 * `tbl:<paraId de son premier paragraphe>` (§16.1 de `superdoc-document-api.md`). Une révision ancrée sur un autre
 * paragraphe du tableau ne prouve rien : aucune n'a été mesurée.
 *
 * Seuls ces deux sous-types prouvent. Une suppression du texte de la première cellule, ou de la seule première ligne
 * d'un tableau qui en a plusieurs, s'ancrerait sur ce même paragraphe et laisserait le reste du tableau en place.
 * `lignes` est lu avant la suppression (`tables.get`) ; illisible, une suppression de ligne ne prouve rien.
 */
function preuveDeSuppression(block: CanvasSnapshotBlock, post: CanvasSnapshot, revisionsAvant: readonly string[], lignes: number | undefined): PreuveDeSuppression {
  const remaining = post.blocks.find((candidate) => candidate.nodeId === block.nodeId);
  if (remaining === undefined || remaining.fullText === '') return { nodeId: block.nodeId, issue: 'retire_ou_vide' };
  if (block.nodeType !== 'table') return { nodeId: block.nodeId, issue: 'non_prouve' };
  const sousTypes = lignes === 1 ? ['table-deletion', 'table-row-deletion'] : ['table-deletion'];
  const revisions = post.trackedChanges
    .filter((revision) => revision.blockId !== undefined && `tbl:${revision.blockId}` === block.nodeId
      && !revisionsAvant.includes(revision.id)
      && revision.type === 'structural' && revision.subtype !== undefined && sousTypes.includes(revision.subtype))
    .map((revision) => revision.id);
  return revisions.length === 0 ? { nodeId: block.nodeId, issue: 'non_prouve' } : { nodeId: block.nodeId, issue: 'tableau_marque', revisions };
}

/** Le nombre de lignes d'un tableau selon `tables.get` ; `undefined` : l'éditeur ne le dit pas. */
async function lireLeNombreDeLignes(ctx: CanvasActionContext, nodeId: string): Promise<number | undefined> {
  try {
    const lu: unknown = await ctx.doc.tables?.get?.({ nodeId });
    const rows = typeof lu === 'object' && lu !== null ? (lu as { rows?: unknown }).rows : undefined;
    return typeof rows === 'number' && Number.isInteger(rows) ? rows : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Les images du corps que porte la plage à supprimer, ou aucune quand elles ne
 * se lisent pas.
 *
 * L'éditeur refuse de supprimer un paragraphe qui porte une image
 * (`paragraph-relationship-bearing-unsupported`, mesuré le 2026-10-12) et ne
 * dit ni laquelle ni quoi faire : l'agent a ainsi laissé la signature d'une
 * autre personne sous un nom qu'il venait de changer (#2141). Lire les images
 * avant d'écrire permet de les nommer. Une lecture en échec ne bloque pas la
 * suppression : l'éditeur garde son propre refus.
 */
async function imagesDesBlocsASupprimer(ctx: CanvasActionContext, nodeIds: readonly string[]): Promise<ImageDuCorps[]> {
  try {
    const images = await listerLesImages(ctx.doc);
    if (images === undefined || images.length === 0) return [];
    const projection = await lireProjectionMarkdown(ctx.doc, 'final');
    return imagesPorteesParLesBlocs(projection, imagesDuCorps(images, projection.blocsDuCorps), nodeIds);
  } catch (erreur) {
    if (estArretDeLot(erreur)) throw erreur;
    if (!isCanvasProjectionReadError(erreur)) console.warn('[canvas] delete_block_range could not list the images of its target blocks; the editor keeps its own refusal.', erreur);
    return [];
  }
}

export async function executeDeleteBlockRange(ctx: CanvasActionContext, input: Extract<TextActionInput, { action: 'delete_block_range' }>): Promise<CanvasActionReceipt> {
  const from = await resolveTarget(ctx, input.from);
  const to = input.to === undefined ? undefined : await resolveTarget(ctx, input.to);
  if (!from.ok) return summaryForFailure(ctx, input.action, from.code, from.message);
  if (to !== undefined && !to.ok) return summaryForFailure(ctx, input.action, to.code, to.message);
  const targets = [from.target, ...(to?.ok && to.target.nodeId !== from.target.nodeId ? [to.target] : [])];
  const preRead = await snapshotWithCompleteTrackedChanges(ctx, { offset: Math.min(...targets.map((target) => target.ordinal)) - 1, limit: Math.abs(targets.at(-1)!.ordinal - targets[0]!.ordinal) + 1 });
  if (!preRead.ok) {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(preRead.snapshot),
      code: 'TRACKED_CHANGES_SNAPSHOT_UNVERIFIABLE',
      message: unverifiedTrackedChangesMessage(input.action, preRead.reason),
    });
  }
  const pre = preRead.snapshot;
  // The two endpoints are resolved through the public-identity guard above,
  // but this verb deletes the entire raw interval. An unavailable block between
  // them must remain readable-only instead of becoming an implicit target.
  const firstOrdinal = Math.min(...targets.map((target) => target.ordinal));
  const lastOrdinal = Math.max(...targets.map((target) => target.ordinal));
  if (!areTargetsInFinalInspection(pre.blocks, ctx.inspectedPublicNodeIds)
    || !isCompletePublicRange(pre, firstOrdinal, lastOrdinal, ctx.inspectedPublicNodeIds)) {
    return summaryForFailure(ctx, input.action, 'TARGET_NOT_IN_FINAL_INSPECTION', messageRangeNotInFinalInspection());
  }
  const imagesPortees = await imagesDesBlocsASupprimer(ctx, pre.blocks.map((block) => block.nodeId));
  if (imagesPortees.length > 0) {
    const nommees = imagesPortees.slice(0, 10).map((image) => `${image.imageId} (block ${image.nodeId}, ${image.width}×${image.height} px, ${image.placement})`).join('; ');
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      code: 'BLOCK_CARRIES_IMAGE',
      message: `delete_block_range refused before any write: the targeted range carries ${imagesPortees.length} image(s) — ${nommees}${imagesPortees.length > 10 ? '; and more' : ''}. `
        + 'Deleting a block never removes an image on your behalf. If the image must go, remove it first with edit_image {imageId, remove:true}, then resend this deletion; '
        + (modeDeRevision(ctx) === 'tracked'
          ? 'change tracking is on, so the emptied paragraph can only be deleted once the lawyer has accepted that image removal. '
          : '')
        + 'If the image must stay, keep its block and delete only the blocks around it.',
      recovery: { action: 'retry', reason: 'Refused before any write: nothing was deleted and the inspected scope is intact. Call edit_image on the image first, or narrow the range, without re-reading.' },
    });
  }
  const revisionsAvant = idsDeRevision(pre.trackedChanges);
  const address = (target: CanvasSnapshotBlock) => ({ kind: 'block', nodeType: target.nodeType, nodeId: target.nodeId });
  const withdrawalPlan = ctx.agentTrackedInsertionLedger?.planWithdrawal({
    revision: pre.revision,
    blocks: pre.blocks,
    trackedChanges: pre.trackedChanges,
  }) ?? { kind: 'none' as const };
  if (withdrawalPlan.kind === 'blocked') {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      code: withdrawalPlan.code,
      message: withdrawalPlan.message,
    });
  }
  // An exhaustive read may still contain a revision whose provider navigation
  // target is unavailable. Without an anchor, it cannot be proved unrelated to
  // the target range after a reload, so letting blocks.delete proceed would be
  // a destructive guess. A same-mount ledger already rejects this shape; this
  // branch closes the ordinary/remount path too.
  const unanchoredTrackedRevisions = withdrawalPlan.kind === 'none'
    ? pre.trackedChanges.filter((revision) => revision.blockId === undefined)
    : [];
  if (unanchoredTrackedRevisions.length > 0) {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      code: 'TRACKED_CHANGES_RELATION_UNVERIFIABLE',
      message: `delete_block_range refused before calling blocks.delete because ${unanchoredTrackedRevisions.length} tracked revision(s) cannot be related to or excluded from the target range: ${unanchoredTrackedRevisions.map((revision) => `changeId=${revision.id}`).join('; ')}. Nothing was deleted or automatically rejected. Run canvas_inspect with trackedChanges and use an explicit accept_tracked_changes or reject_tracked_changes action with an exact published id if a review decision is intentional.`,
    });
  }
  // SuperDoc exposes the connected lawyer as author for both lawyer and agent
  // suggestions. Therefore an open paragraph-insertion wrapper may only be
  // withdrawn automatically when the instance-local ledger proves the exact
  // create.paragraph -> immediate reread -> nodeId/changeId causal chain.
  // Falling through to blocks.delete would reproduce the provider's
  // paragraph-tracked-wrapper-unsupported failure and, for a mixed range,
  // could leave an irreversible partial deletion before the error is known.
  const unprovenTrackedRevisions = withdrawalPlan.kind === 'none'
    ? pre.blocks.flatMap((block) => pre.trackedChanges
      .filter((revision) => revision.blockId === block.nodeId)
      .map((revision) => ({ nodeId: block.nodeId, changeId: revision.id })))
    : [];
  if (unprovenTrackedRevisions.length > 0) {
    const described = unprovenTrackedRevisions
      .map(({ nodeId, changeId }) => `nodeId=${nodeId}, changeId=${changeId}`)
      .join('; ');
    const onlyKnownParagraphInsertions = unprovenTrackedRevisions.every(({ nodeId, changeId }) =>
      pre.trackedChanges.some((revision) => revision.id === changeId && isOpenParagraphInsertionRevision(revision, nodeId)));
    const blocsSansRevision = pre.blocks
      .filter((block) => unprovenTrackedRevisions.every(({ nodeId }) => nodeId !== block.nodeId))
      .map((block) => `nodeId=${block.nodeId}`);
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      code: onlyKnownParagraphInsertions
        ? 'TRACKED_PARAGRAPH_INSERTION_PROVENANCE_UNAVAILABLE'
        : 'TRACKED_BLOCK_REVIEW_PROVENANCE_UNAVAILABLE',
      message: onlyKnownParagraphInsertions
        ? `delete_block_range refused before calling blocks.delete because the target contains ${unprovenTrackedRevisions.length} open tracked paragraph insertion(s) without same-mount agent provenance: ${described}. The displayed lawyer author cannot prove ownership. Nothing was deleted or automatically rejected; do not retry this deletion unchanged: it will be refused again. `
          + 'A paragraph that is still a pending tracked insertion is not deleted as a block: it leaves the document when its insertion is rejected. '
          + `Next step, only if removing ${unprovenTrackedRevisions.length === 1 ? 'this paragraph' : 'these paragraphs'} is what the lawyer asked: reject_tracked_changes with target:{kind:"ids",ids:${JSON.stringify([...new Set(unprovenTrackedRevisions.map(({ changeId }) => changeId))])}} — rejecting a paragraph insertion removes the whole paragraph, with everything typed in it since.`
          + (blocsSansRevision.length === 0 ? '' : ` The other block(s) of the range carry no pending change (${blocsSansRevision.join(', ')}): delete them with a new delete_block_range once the rejection is done.`)
        : `delete_block_range refused before calling blocks.delete because the target contains ${unprovenTrackedRevisions.length} tracked revision(s) with no same-mount agent provenance: ${described}. Their provider taxonomy may be unknown or incomplete, so a raw block deletion is not safe. Nothing was deleted or automatically rejected; inspect tracked changes and use an explicit accept_tracked_changes or reject_tracked_changes action with the exact published id if a review decision is intentional.`,
    });
  }
  if (withdrawalPlan.kind === 'reject' && typeof ctx.doc.trackChanges?.decide !== 'function') {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      code: 'CAPABILITY_UNAVAILABLE',
      message: 'delete_block_range identified a verified agent paragraph insertion that must be withdrawn by rejecting its exact tracked revision, but editor.doc.trackChanges.decide is unavailable. Nothing was modified.',
    });
  }
  if (withdrawalPlan.kind === 'none' && typeof ctx.doc.blocks?.delete !== 'function') {
    return abortedBeforeMutation({ intent: input.action, snapshot: toCanvasSnapshotSummary(pre), code: 'CAPABILITY_UNAVAILABLE', message: 'delete_block_range requires editor.doc.blocks.delete to remove blocks.' });
  }
  // A paragraph created in the same agent-controlled revision chain is not a
  // lawyer-authored suggestion just because SuperDoc v2 displays the lawyer as
  // author. Deleting that still-open qualified paragraph-insertion wrapper is
  // the provider edge case that fails with `paragraph-tracked-wrapper-unsupported`;
  // rejecting its exact recorded revision withdraws the proposal without
  // manufacturing a misleading accept-then-delete history. The provider accepts
  // several exact ids as one atomic review decision, so this path deliberately
  // has no per-paragraph await where a lawyer edit could interleave.
  if (withdrawalPlan.kind === 'reject') {
    const withdrawnChangeIds = withdrawalPlan.entries.map((entry) => entry.changeId);
    const attendue = expectedRevisionFor(ctx, pre.revision);
    let provider: unknown;
    try {
      provider = await trackedMutation(ctx, attendue, 'delete_block_range:withdraw-agent-created-range', () =>
        ctx.doc.trackChanges?.decide?.(
          { decision: 'reject', target: { kind: 'ids', ids: withdrawnChangeIds } },
          { changeMode: modeDeRevision(ctx), expectedRevision: attendue },
        ));
    } catch (error) {
      if (estArretDeLot(error)) throw error;
      provider = {
        success: false,
        failure: {
          code: 'PROVIDER_EXCEPTION',
          message: error instanceof Error ? error.message.slice(0, 200) : String(error).slice(0, 200),
        },
      };
    }
    const postRead = await snapshotWithCompleteTrackedChanges(ctx, {
      offset: Math.max(0, pre.blocks[0]!.ordinal - 1),
      limit: Math.max(1, pre.blocks.length),
    });
    if (!postRead.ok) {
      ctx.verifierEpoque('delete_block_range:withdrawal-post-review-read');
      return inconclusiveAfterMutation({
        intent: input.action,
        snapshot: toCanvasSnapshotSummary(pre),
        postSnapshot: toCanvasSnapshotSummary(postRead.snapshot),
        resolvedTargets: pre.blocks.map((block) => targetSummary(block)),
        code: 'TRACKED_CHANGES_SNAPSHOT_UNVERIFIABLE',
        message: `delete_block_range submitted an atomic review withdrawal, but its complete post-decision review state could not be proven: ${postRead.reason}. The withdrawal may or may not have settled; do not retry it.`,
      });
    }
    const post = postRead.snapshot;
    const providerFailed = typeof provider === 'object'
      && provider !== null
      && (provider as { success?: unknown }).success === false;
    if (!providerFailed && withdrawnChangeIds.every((changeId) => !idsDeRevision(post.trackedChanges).includes(changeId))) {
      ctx.agentTrackedInsertionLedger?.remove(withdrawalPlan.entries.map((entry) => entry.nodeId));
    }
    ctx.verifierEpoque('delete_block_range:withdrawal-receipt');
    return buildVerifiedReceipt({
      intent: input.action,
      regimeSuivi: ctx.regimeSuivi,
      preSnapshot: toCanvasSnapshotSummary(pre),
      postSnapshot: toCanvasSnapshotSummary(post),
      resolvedTargets: pre.blocks.map((block) => targetSummary(block)),
      provider,
      revisionsAvant,
      revisionsApres: idsDeRevision(post.trackedChanges),
      identitesAttendues: { disparues: withdrawnChangeIds },
      mouvementAttendu: 'apparition-ou-disparition',
      verification: [
        {
          name: 'agent_insertions_withdrawn',
          passed: pre.blocks.every((block) => {
            const remaining = post.blocks.find((candidate) => candidate.nodeId === block.nodeId);
            return remaining === undefined || remaining.fullText === '';
          }),
          message: 'Every block backed by the exact rejected agent insertion must be gone or show empty visible text after the atomic withdrawal.',
        },
        {
          name: 'atomic_withdrawal_provider_call',
          passed: !providerFailed,
          message: providerFailed
            ? 'The provider rejected the single atomic review decision; no second withdrawal was attempted.'
            : 'The provider accepted one atomic review decision covering every causally verified agent insertion in the range.',
        },
      ],
      effects: [{
        kind: 'delete_block_range',
        reviewability: 'tracked',
        description: 'Withdrew the resolved agent-created paragraph range by atomically rejecting its exact open qualified paragraph-insertion revisions.',
        nodeIds: pre.blocks.map((block) => block.nodeId),
      }],
    });
  }

  // SuperDoc ≥1.42 tracks per-block deletes but blocks.deleteRange still throws
  // "does not support tracked mode" (hit live in conv 6b3d5ec4, 2026-07-13) — so a
  // native range is deleted one block at a time, in reverse order so earlier
  // deletions cannot shift the remaining targets. This is deliberately only the
  // ordinary-block path: agent-created tracked insertions returned above in one
  // atomic review decision.
  const lignesDesTableaux = new Map<string, number | undefined>();
  for (const block of pre.blocks.filter(({ nodeType }) => nodeType === 'table')) lignesDesTableaux.set(block.nodeId, await lireLeNombreDeLignes(ctx, block.nodeId));
  const outcomes: Array<{ nodeId: string; ok: boolean; detail?: string }> = [];
  for (const block of [...pre.blocks].reverse()) {
    try {
      // Verrou optimiste : CHAQUE suppression fait avancer la révision, donc
      // l'`expectedRevision` de la pré-lecture ne vaut que pour la première
      // (live : REVISION_MISMATCH « expected 2 but document is at 3 » sur le
      // bloc #2). Les suivantes relisent la révision RÉELLE, parce que ce chemin
      // ne contient aucune décision causale d'agent à préserver atomiquement.
      const attendue = outcomes.length === 0
        ? expectedRevisionFor(ctx, pre.revision)
        : await revisionCourante(ctx, pre.revision);
      // L'éditeur refuse de supprimer un bloc qui porte un champ Word vide (`champsVides.ts`) : ils partent d'abord.
      const champsRetires = await retirerLesChampsVidesDuBloc(ctx, block.nodeId, attendue);
      const pourSupprimer = champsRetires > 0 ? await revisionCourante(ctx, pre.revision) : attendue;
      const result = await trackedMutation(ctx, pourSupprimer, `delete_block_range:${block.nodeId}`, () =>
        ctx.doc.blocks?.delete?.({ target: address(block) }, { changeMode: modeDeRevision(ctx), expectedRevision: pourSupprimer }));
      const failed = typeof result === 'object' && result !== null && (result as { success?: unknown }).success === false;
      if (!failed) {
        outcomes.push({ nodeId: block.nodeId, ok: true });
      } else {
        const failure = (result as { failure?: unknown }).failure;
        outcomes.push({ nodeId: block.nodeId, ok: false, detail: JSON.stringify(failure ?? 'provider returned success:false').slice(0, 160) });
      }
    } catch (error) {
      if (estArretDeLot(error)) throw error;
      outcomes.push({ nodeId: block.nodeId, ok: false, detail: error instanceof Error ? error.message.slice(0, 200) : String(error).slice(0, 200) });
    }
    signalerLAvancee(ctx, outcomes.length, pre.blocks.length);
  }
  const post = await snapshot(ctx, { offset: Math.max(0, pre.blocks[0]!.ordinal - 1), limit: Math.max(1, pre.blocks.length) });
  const deletedNodeIds = pre.blocks.map((block) => block.nodeId);
  const failedOutcomes = outcomes.filter((outcome) => !outcome.ok);
  const preuves = pre.blocks.map((block) => preuveDeSuppression(block, post, revisionsAvant, lignesDesTableaux.get(block.nodeId)));
  const nonProuves = preuves.filter((preuve) => preuve.issue === 'non_prouve');
  const tableauxMarques = preuves.filter((preuve) => preuve.issue === 'tableau_marque');
  ctx.verifierEpoque('delete_block_range:receipt');
  return buildVerifiedReceipt({
    intent: input.action,
    regimeSuivi: ctx.regimeSuivi,
    preSnapshot: toCanvasSnapshotSummary(pre),
    postSnapshot: toCanvasSnapshotSummary(post),
    // Lambda explicite : voir `executeReplaceText` — l'index d'`Array.map`
    // devenait `candidateCount: 0` et invalidait chaque receipt de suppression.
    resolvedTargets: pre.blocks.map((block) => targetSummary(block)),
    provider: { steps: outcomes },
    revisionsAvant,
    revisionsApres: idsDeRevision(post.trackedChanges),
    // Une suppression suivie ajoute une marque de suppression OU annule une
    // insertion encore en attente — les deux sont des issues légitimes,
    // mesurées sur le vrai SuperDoc.
    mouvementAttendu: 'apparition-ou-disparition',
    verification: [
      {
        name: 'blocks_deleted_or_marked',
        passed: nonProuves.length === 0,
        message: nonProuves.length > 0
          ? `${nonProuves.length} of the ${preuves.length} block(s) of the interval still show their text after the deletion and carry no new deletion revision: ${nonProuves.map(({ nodeId }) => `nodeId=${nodeId}`).join(', ')}. A deleted block must be gone, show empty visible text (tracked deletion), or — for a table — carry a new tracked deletion anchored on it.`
          : tableauxMarques.length > 0
            ? `Every block of the interval is deleted. ${tableauxMarques.map(({ nodeId, revisions }) => `Table nodeId=${nodeId} keeps its text and is marked deleted by tracked change(s) ${revisions.join(', ')}`).join('; ')}: it leaves the document when the lawyer accepts the deletion. Do not delete it again.`
            : 'Every block in the requested deletion interval is gone or shows empty visible text (tracked deletion).',
      },
      {
        name: 'delete_provider_calls',
        passed: failedOutcomes.length === 0,
        message: failedOutcomes.length === 0
          ? 'Every per-block blocks.delete call succeeded.'
          : `blocks.delete failed for: ${failedOutcomes.map((outcome) => `${outcome.nodeId} → ${outcome.detail ?? 'unknown failure'}`).join('; ')}. The other blocks in the range were still deleted.`,
      },
    ],
    effects: [{
      kind: 'delete_block_range',
      reviewability: 'tracked',
      description: 'Deleted the resolved block range, one tracked block deletion per block.',
      nodeIds: deletedNodeIds,
    }],
  });
}

async function executeTrackedDecision(ctx: CanvasActionContext, input: Extract<TextActionInput, { action: 'accept_tracked_changes' | 'reject_tracked_changes' }>): Promise<CanvasActionReceipt> {
  // `kind:'all'` (décision « tout accepter », hub redaction-web-canvas, 2026-09-30) reuses this exact fenced path: the complete
  // pre-read below becomes the selection, so every post-state check (partial,
  // unattributed, provider failure) keeps its meaning without a second executor.
  const toutes = input.target.kind === 'all';
  const preRead = await snapshotWithCompleteTrackedChanges(ctx);
  if (!preRead.ok) {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(preRead.snapshot),
      code: 'TRACKED_CHANGES_SNAPSHOT_UNVERIFIABLE',
      message: unverifiedTrackedChangesMessage(input.action, preRead.reason),
    });
  }
  const pre = preRead.snapshot;
  const activeBefore = idsDeRevision(pre.trackedChanges);
  // Duplicate ids are deduplicated before the provider call: SuperDoc 2.15.1
  // does not document how `decide({kind:'ids'})` treats a repeated id, and the
  // receipt must count the distinct revisions the lawyer actually asked for.
  // For `all`, the complete pre-read (every story, every page, bound to the
  // document revision) IS the selection the provider will settle.
  const ids = input.target.kind === 'all' ? activeBefore : [...new Set(input.target.ids)];
  if (toutes && ids.length === 0) {
    return abortedBeforeMutation({
      intent: input.action, snapshot: toCanvasSnapshotSummary(pre), code: 'NO_OP',
      message: `${input.action} target:{kind:"all"} found 0 open tracked changes in the complete review read (all stories). Nothing was modified; there is nothing to ${input.action === 'accept_tracked_changes' ? 'accept' : 'reject'}.`,
      // Re-inspecting would read the same empty review state: say so instead
      // of letting the default `reinspect` recovery send the agent in a loop.
      nextAction: 'Tell the lawyer the document has no open tracked changes; do not re-inspect or retry this decision.',
      recovery: { action: 'stop', reason: 'No open tracked change exists; the decision has nothing to settle.' },
    });
  }
  if (typeof ctx.doc.trackChanges?.decide !== 'function') {
    return abortedBeforeMutation({
      intent: input.action, snapshot: toCanvasSnapshotSummary(pre), code: 'CAPABILITY_UNAVAILABLE',
      message: `${input.action} requires editor.doc.trackChanges.decide, which this editor does not expose. Nothing was modified and retrying the same call will fail identically.`,
    });
  }
  // ⚠️ Ce refus est LU PAR UN MODÈLE, hors contexte (#994). Avant ce ticket il
  // disait « Every requested tracked-change id must be active » sans nommer
  // l'identifiant fautif ni le moyen d'en obtenir un valide — et il n'existait
  // AUCUNE surface qui publie ces identifiants, si bien que le seul exemple
  // qu'avait le modèle était `'change-1'`, une valeur inventable qu'il pouvait
  // recopier. Le message nomme donc les identifiants inconnus, dit combien de
  // révisions sont réellement ouvertes, et donne l'appel exact qui les rend.
  const inconnus = ids.filter((id) => !activeBefore.includes(id));
  if (inconnus.length > 0) {
    return abortedBeforeMutation({
      intent: input.action, snapshot: toCanvasSnapshotSummary(pre), code: 'SELECTOR_NOT_FOUND',
      message: `${inconnus.length} of the ${ids.length} requested tracked-change ids do not exist in this document: ${describeTrackedChangeIds(inconnus)}. `
        + `The document currently has ${activeBefore.length} open tracked change(s). `
        + 'Tracked-change ids are opaque provider strings and can NEVER be guessed or composed: '
        + 'run canvas_inspect with includeDomains including "trackedChanges" and reuse the "id" values it returns, verbatim. '
        + 'Nothing was modified.',
    });
  }
  const decision = input.action === 'accept_tracked_changes' ? 'accept' : 'reject';
  const requestedIdsDescription = toutes
    ? `target:{kind:"all"} (the ${ids.length} tracked change(s) open before the call, all stories)`
    : describeTrackedChangeIds(ids);
  const attendue = expectedRevisionFor(ctx, pre.revision);
  // Source: https://github.com/superdoc/docx-editor/blob/main/packages/document-api/src/track-changes/track-changes.ts (verified 2026-09-28)
  // `kind:'ids'` resolves several logical changes in one atomic history unit.
  // The complete pre-read above fences every selected id before this single
  // mutation; a per-id loop would reopen an interleaving window after each
  // decision and could settle only a prefix of this homogeneous instruction.
  let provider: unknown;
  try {
    // `story:'all'` matches the pre-read scope (`trackChanges.list({in:'all'})`):
    // without it the provider would settle the body only, and header/footer
    // revisions would surface as a false "partial" state.
    // Source: superdoc/dist/document-api/src/track-changes/track-changes.d.ts `ReviewDecideTarget` (verified 2026-09-30, superdoc 2.15.1)
    provider = await trackedMutation(ctx, attendue, `${decision}:${toutes ? 'all' : 'ids'}`, () =>
      ctx.doc.trackChanges?.decide?.(
        { decision, target: toutes ? { kind: 'all', story: 'all' } : { kind: 'ids', ids } },
        { changeMode: modeDeRevision(ctx), expectedRevision: attendue },
      ));
  } catch (error) {
    if (estArretDeLot(error)) throw error;
    provider = {
      success: false,
      failure: {
        code: 'PROVIDER_EXCEPTION',
        message: error instanceof Error ? error.message.slice(0, 200) : String(error).slice(0, 200),
      },
    };
  }
  const postRead = await snapshotWithCompleteTrackedChanges(ctx);
  if (!postRead.ok) {
    ctx.verifierEpoque(`${decision}:post-review-read`);
    return inconclusiveAfterMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      postSnapshot: toCanvasSnapshotSummary(postRead.snapshot),
      code: 'TRACKED_CHANGES_SNAPSHOT_UNVERIFIABLE',
      message: `${input.action} was submitted for ${requestedIdsDescription}, but the complete post-decision tracked-change state could not be proven: ${postRead.reason}. The decision may or may not have settled; do not retry it.`,
    });
  }
  const post = postRead.snapshot;
  const activeAfter = idsDeRevision(post.trackedChanges);
  const providerFailed = typeof provider === 'object'
    && provider !== null
    && (provider as { success?: unknown }).success === false;
  const settledRequestedIds = ids.filter((id) => !activeAfter.includes(id));
  const disappearedIds = activeBefore.filter((id) => !activeAfter.includes(id));
  const unselectedDisappearedIds = disappearedIds.filter((id) => !ids.includes(id));
  const allRequestedIdsSettled = settledRequestedIds.length === ids.length;
  // A settled insertion must stop being treated as an open agent proposal.
  // This is a review-state update only: it neither saves the document nor
  // certifies its DOCX bytes. If a later agent asks to delete the accepted
  // paragraph, it follows the ordinary deletion path instead of first failing
  // on stale causal provenance.
  if (allRequestedIdsSettled) {
    ctx.agentTrackedInsertionLedger?.forgetSettledRevisions(ids);
  }
  // A failed provider result never says which ids, if any, were settled. A
  // changed post-state therefore cannot be attributed to this batch rather
  // than an interleaving lawyer action. Do not turn that uncertainty into a
  // per-id retry: another decision could settle a different revision or repeat
  // an effect already visible in the document.
  if (providerFailed && disappearedIds.length > 0) {
    ctx.verifierEpoque(`${decision}:failed-provider-state`);
    return inconclusiveAfterMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      postSnapshot: toCanvasSnapshotSummary(post),
      code: 'TRACKED_CHANGES_DECISION_STATE_INDETERMINATE',
      message: `${input.action} submitted one atomic decision for ${requestedIdsDescription}, but ${describeTrackedChangeProviderFailure(provider)} while the complete post-read showed ${describeTrackedChangeIds(disappearedIds)} no longer open. The provider result does not identify which ids it settled, so this state cannot be attributed safely. Do not retry or fall back to per-id decisions; inspect the current tracked changes before any later review action.`,
    });
  }
  if (unselectedDisappearedIds.length > 0) {
    ctx.verifierEpoque(`${decision}:unexpected-post-state`);
    return inconclusiveAfterMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      postSnapshot: toCanvasSnapshotSummary(post),
      code: 'TRACKED_CHANGES_DECISION_STATE_INDETERMINATE',
      message: `${input.action} submitted one atomic decision for ${requestedIdsDescription}; the complete post-read shows ${allRequestedIdsSettled ? 'every selected id settled' : `selected ${describeTrackedChangeIds(ids.filter((id) => activeAfter.includes(id)))} still open`} but also shows unselected tracked-change ${describeTrackedChangeIds(unselectedDisappearedIds)} no longer open. Exact scope cannot be attributed safely. Do not retry or repair this with per-id decisions; inspect the current tracked changes before any later review action.`,
    });
  }
  if (providerFailed) {
    const receipt = buildVerifiedReceipt({
      intent: input.action,
      regimeSuivi: ctx.regimeSuivi,
      preSnapshot: toCanvasSnapshotSummary(pre),
      postSnapshot: toCanvasSnapshotSummary(post),
      resolvedTargets: [],
      provider,
      revisionsAvant: activeBefore,
      revisionsApres: activeAfter,
      identitesAttendues: { disparues: ids },
      verification: [],
      // No effect line: the model quotes it to the lawyer, and nothing was
      // accepted or rejected on this branch (every selected id is still open).
      effects: [],
    });
    ctx.verifierEpoque(`${decision}:failed-provider-receipt`);
    return {
      ...receipt,
      errors: [{
        code: 'TRACKED_CHANGES_PROVIDER_REJECTED',
        message: `${input.action} submitted one atomic decision for ${requestedIdsDescription}, but ${describeTrackedChangeProviderFailure(provider)} and the complete post-read still shows every selected id open. Nothing was retried or split into per-id decisions. Resolve the provider permission or document state before a new, freshly inspected review action.`,
      }],
      recovery: {
        action: 'stop',
        reason: 'The provider rejected the atomic review decision; it must not be replayed automatically.',
      },
    };
  }
  if (!allRequestedIdsSettled && settledRequestedIds.length > 0) {
    ctx.verifierEpoque(`${decision}:partial-provider-state`);
    return inconclusiveAfterMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      postSnapshot: toCanvasSnapshotSummary(post),
      code: 'TRACKED_CHANGES_DECISION_STATE_INDETERMINATE',
      message: `${input.action} submitted one atomic decision for ${requestedIdsDescription}, but the complete post-read shows only ${describeTrackedChangeIds(settledRequestedIds)} no longer open. An atomic decision must not be repaired with per-id retries; inspect the current tracked changes before any later review action.`,
    });
  }
  ctx.verifierEpoque(`${decision}:receipt`);
  return buildVerifiedReceipt({
    intent: input.action,
    regimeSuivi: ctx.regimeSuivi,
    preSnapshot: toCanvasSnapshotSummary(pre),
    postSnapshot: toCanvasSnapshotSummary(post),
    resolvedTargets: [],
    provider,
    // Une décision RETIRE des révisions au lieu d'en ajouter : ce sont donc les
    // identités demandées, disparues, qui prouvent l'effet.
    revisionsAvant: activeBefore,
    revisionsApres: activeAfter,
    identitesAttendues: { disparues: ids },
    verification: [],
    effects: [{
      kind: decision,
      reviewability: 'tracked',
      description: toutes
        ? `${decision}ed all tracked changes: ${ids.length} open before, ${activeAfter.length} open after.`
        : `${decision}ed explicitly selected tracked changes.`,
    }],
  });
}

export async function executeAcceptTrackedChanges(ctx: CanvasActionContext, input: Extract<TextActionInput, { action: 'accept_tracked_changes' }>): Promise<CanvasActionReceipt> {
  return executeTrackedDecision(ctx, input);
}

export async function executeRejectTrackedChanges(ctx: CanvasActionContext, input: Extract<TextActionInput, { action: 'reject_tracked_changes' }>): Promise<CanvasActionReceipt> {
  return executeTrackedDecision(ctx, input);
}

export async function executeUndoChanges(ctx: CanvasActionContext, input: Extract<TextActionInput, { action: 'undo_changes' }>): Promise<CanvasActionReceipt> {
  const pre = await snapshot(ctx);
  if (typeof ctx.doc.history?.undo !== 'function') {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      code: 'CAPABILITY_UNAVAILABLE',
      message: 'undo_changes requires editor.doc.history.undo (l\'undo vit sous history, jamais à la racine du Document API).',
    });
  }
  // Les zones d'en-tête et de pied, relevées avant et après : une annulation au compte trop court peut défaire
  // la copie d'une partie sans défaire le réglage de section qui la rendait nécessaire (`pagesSansLeurPartie.ts`).
  const zonesAvant = await lireLesZonesDePage(ctx.doc);
  const provider: unknown[] = [];
  for (let step = 0; step < input.steps; step++) {
    const attendue = step === 0 ? expectedRevisionFor(ctx, pre.revision) : await revisionCourante(ctx, pre.revision);
    // ⚠️ Aucun argument : `history.undo()` ne prend ni input ni options. La
    // révision attendue est tout de même calculée et passée à `trackedMutation`,
    // qui la relit APRÈS l'appel — c'est là, et là seulement, que le verrou
    // optimiste subsiste sur ce chemin.
    provider.push(await trackedMutation(ctx, attendue, `undo:${step + 1}`, () => ctx.doc.history?.undo?.()));
  }
  // Le reçu dit combien d'étapes ont RÉELLEMENT été annulées : `history.undo`
  // rend un rejet sous la forme `{status:'rejected'}` (éditeur en lecture seule
  // pendant un export), que `success === false` seul ne voit pas — le reçu
  // annonçait alors « Undid 2 » pour une seule étape annulée (#1860).
  // Passé la dernière entrée de son historique, il rend `{noop:true,
  // reason:'EMPTY_UNDO_STACK'}` (`mesure-format-paragraphs`, 2026-10-02) : rien
  // n'a été annulé, et le reçu ne le compte pas (#1866).
  const rienAAnnuler = (result: unknown): boolean => estObjet(result) && result.noop === true;
  const etapesAnnulees = provider.filter((result) => result !== undefined && !estUnEchecDuFournisseur(result) && !rienAAnnuler(result)).length;
  const post = await snapshot(ctx);
  ctx.verifierEpoque('undo_changes:receipt');
  const zonesApres = zonesAvant === null ? null : await lireLesZonesDePage(ctx.doc);
  const privees = zonesAvant === null || zonesApres === null ? [] : pagesPriveesDUnePartie(zonesAvant, zonesApres);
  const recu = buildVerifiedReceipt({
    intent: input.action,
    regimeSuivi: ctx.regimeSuivi,
    preSnapshot: toCanvasSnapshotSummary(pre),
    postSnapshot: toCanvasSnapshotSummary(post),
    resolvedTargets: [],
    provider,
    // `undo` ne pose pas de révision suivie : sa preuve reste le mouvement de la
    // révision du document, comparée aux valeurs relues avant et après.
    revisionAvant: pre.revision,
    revisionApres: post.revision,
    verification: [
      {
        name: 'undo_result',
        passed: etapesAnnulees === input.steps,
        message: etapesAnnulees === input.steps
          ? 'Each undo call returned a successful provider result.'
          : `Only ${etapesAnnulees} of ${input.steps} undo call(s) succeeded; the others were rejected by the editor, or found nothing left to undo. `
            + 'Do not replay undo_changes with the same steps: run canvas_inspect and undo only what is still to undo.',
      },
      { name: 'revision_changed', passed: post.revision !== pre.revision, message: 'Undo must advance the document revision.' },
    ],
    effects: [{
      kind: 'undo',
      reviewability: 'none',
      description: etapesAnnulees === input.steps
        ? `Undid ${input.steps} recent change(s).`
        : `Undid ${etapesAnnulees} of ${input.steps} requested change(s).`,
    }],
  });
  const controle = {
    name: 'pages_gardent_leurs_parties',
    passed: privees.length === 0,
    message: privees.length === 0
      ? 'After the undo, no page lost a header or footer it was showing while its section setting stayed on.'
      : `After the undo, ${privees.length} page set(s) no longer show a header or footer they were showing.`,
  };
  if (privees.length === 0) return { ...recu, verification: [...recu.verification, controle] };
  const dites = privees.map((privee) => `${privee.zone === 'first_page' ? 'la première page' : 'les pages paires'} de la section `
    + `${privee.section_index} ne montre(nt) plus ${privee.part === 'footer' ? 'le pied de page' : "l'en-tête"}`).join(' ; ');
  // L'annulation demandée a eu lieu : un reçu `ok` devient `partial`, tout autre statut garde le sien et ses erreurs.
  return {
    ...recu,
    status: recu.status === 'ok' ? 'partial' : recu.status,
    verificationPassed: false,
    verification: [...recu.verification, controle],
    errors: [...recu.errors, {
      code: 'UNDO_LEFT_PAGE_WITHOUT_PART',
      // Deux histoires donnent ce même relevé, et l'historique ne dit pas laquelle : une écriture d'en-tête ou de
      // pied défaite à moitié (le réglage qu'elle avait levé reste), ou une zone écrite sur un réglage déjà levé
      // (papier à en-tête) puis défaite en entier. Le message dit le fait et les deux cas ; il ne conseille pas
      // d'annuler davantage sans relecture, ce qui déferait autre chose dans le second cas.
      message: `undo_changes a annulé ${etapesAnnulees} étape(s). Après l'annulation, ${dites}, alors que le réglage de `
        + 'section (première page distincte, ou pages paires) est toujours activé. Deux cas : (1) le réglage était déjà '
        + "activé avant l'écriture annulée (papier à en-tête, Word importé) — l'annulation est complète, rien à faire ; "
        + "(2) l'écriture annulée avait elle-même activé le réglage — elle laisse plusieurs entrées d'historique (réglage, "
        + "copie de l'autre partie, zone, paragraphes) et il en reste une ou deux à défaire. Dis à l'avocat ce que ces "
        + "pages ne montrent plus, et relis l'état avec canvas_inspect avant toute autre annulation. Pour revenir à "
        + "l'état d'avant l'annulation, redo_changes.",
    }],
    recovery: recu.recovery ?? {
      action: 'stop',
      reason: "L'annulation demandée est faite ; le reçu nomme les pages qu'elle laisse sans une partie qu'elles montraient.",
    },
  };
}

/**
 * La fenêtre de relecture de `move_range` — le juge d'un déplacement est un
 * ORDINAL, donc les deux blocs doivent tenir dans la même lecture.
 *
 * 500 est la borne déjà retenue par les verbes de liste pour la même question.
 */
const FENETRE_DE_DEPLACEMENT = { offset: 0, limit: 500 };

/**
 * Déplacer un bloc ailleurs dans le document (#996).
 *
 * ## ⚠️ Le juge est l'ORDINAL relu, JAMAIS le texte
 *
 * Un bloc déplacé garde son texte : seul son rang change. Une postcondition
 * bâtie sur le texte serait donc **aveugle par construction** — elle rendrait
 * vert avec le déplacement comme sans lui. La preuve est l'ordre relatif relu
 * entre la source et la destination.
 *
 * ## Forme d'appel MESURÉE
 *
 * `blocks.move({source, destination, placement})` où `source` et `destination`
 * sont des **adresses de bloc** (`{kind:'block', nodeType, nodeId}`) et
 * `placement` vaut `'before'` ou `'after'`. Les quatre refus rencontrés à côté :
 * « requires a source. », « requires a destination. », « destination.kind must
 * be "block". », « placement must be "before" or "after". » — aucun de ces noms
 * n'était devinable d'un `.d.ts`.
 */
export async function executeMoveRange(ctx: CanvasActionContext, input: Extract<TextActionInput, { action: 'move_range' }>): Promise<CanvasActionReceipt> {
  const source = await resolveTarget(ctx, input.selector);
  if (!source.ok) return summaryForFailure(ctx, input.action, source.code, source.message);
  const destination = await resolveTarget(ctx, input.destination);
  if (!destination.ok) return summaryForFailure(ctx, input.action, destination.code, destination.message);

  const pre = await snapshot(ctx, FENETRE_DE_DEPLACEMENT);

  if (source.target.nodeId === destination.target.nodeId) {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      code: 'SELECTOR_NOT_FOUND',
      message: `move_range a été REFUSÉ avant toute écriture : le sélecteur et la destination désignent le MÊME bloc `
        + `(nodeId=${source.target.nodeId}), donc il n'y a aucun déplacement à faire. Rien n'a été modifié. `
        + 'Vise deux blocs différents — la destination est le bloc AVANT ou APRÈS lequel le passage doit atterrir.',
      nextAction: 'Choisis une destination différente du bloc à déplacer.',
      recovery: {
        action: 'retry',
        reason: 'Rien n\'a été écrit. Ré-inspecter rendrait le même document : la sortie est une autre destination.',
      },
    });
  }

  if (typeof ctx.doc.blocks?.move !== 'function') {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      code: 'CAPABILITY_UNAVAILABLE',
      message: 'move_range exige editor.doc.blocks.move, que cet éditeur n\'expose pas. Rien n\'a été modifié et '
        + 'relancer la même action échouera à l\'identique. Le contournement — supprimer puis réécrire — perdrait la '
        + 'mise en forme, les commentaires ancrés et l\'historique de révision du passage : préviens l\'avocat '
        + 'plutôt que de le faire à son insu.',
    });
  }

  /**
   * ⚠️ **Un déplacement est IMPOSSIBLE tant que le suivi des modifications est
   * actif**, et le refus est posé ici, AVANT toute écriture.
   *
   * Mesuré le 2026-08-16 (`mesure-996-tranche3.spec.ts` §18, témoin positif à
   * l'appui) : sous fenêtre `suggesting`, `blocks.move` rend
   * `CAPABILITY_UNAVAILABLE — "paragraph-move-anchor-content-unsupported"`. 
   * Laisser passer l'appel sous suivi actif ferait échouer le verbe après coup —
   * la pathologie exacte d'`undo_changes`, proposé à chaque tour et refusé à
   * chaque tour.
   *
   * Le refus préalable coûte zéro mutation, et surtout il NOMME la sortie :
   * l'interrupteur de suivi appartient à l'avocat (#990), l'agent ne peut donc
   * que le lui demander — ou renoncer au déplacement au profit d'une
   * suppression-réécriture, qui est suivie mais perd la mise en forme.
   */
  if (ctx.regimeSuivi !== false) {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      code: 'TRACKING_UNSUPPORTED',
      message: 'move_range a été REFUSÉ avant toute écriture : l\'éditeur ne sait PAS déplacer un bloc tant que le '
        + 'suivi des modifications est actif, et ce document l\'a actif. Rien n\'a été modifié, et relancer la même '
        + 'action échouera à l\'identique tant que le réglage n\'a pas changé. Le suivi est le réglage de l\'avocat : tu '
        + 'ne le changes qu\'à sa demande ou avec son accord explicite. Deux sorties — demande-lui s\'il veut couper '
        + 'le suivi, puis, sur son accord, set_review_mode enabled:false et relance ce déplacement ; ou renonce au '
        + 'déplacement et refais le passage par delete_block_range puis insert_paragraphs, qui sont suivis mais '
        + 'perdent la mise en forme d\'origine.',
      nextAction: 'Explique à l\'avocat que le déplacement exige de couper le suivi des modifications, et propose-lui '
        + 'l\'alternative supprimer-puis-réécrire.',
      recovery: {
        action: 'stop',
        reason: 'Rien n\'a été écrit. Aucune relance ni ré-inspection ne changera ce refus : il dépend du suivi des '
          + 'modifications, que tu ne changes qu\'avec l\'accord de l\'avocat.',
      },
    });
  }

  const adresse = (bloc: CanvasSnapshotBlock) => ({ kind: 'block', nodeType: bloc.nodeType, nodeId: bloc.nodeId });
  const revisionsAvant = idsDeRevision(pre.trackedChanges);
  const expectedRevision = expectedRevisionFor(ctx, pre.revision);
  const provider = await trackedMutation(ctx, pre.revision, 'move_range:blocks.move', () =>
    ctx.doc.blocks?.move?.(
      { source: adresse(source.target), destination: adresse(destination.target), placement: input.placement },
      { changeMode: modeDeRevision(ctx), expectedRevision },
    ));

  const post = await snapshot(ctx, FENETRE_DE_DEPLACEMENT);
  ctx.verifierEpoque('move_range:receipt');

  const rangSource = post.blocks.find((bloc) => bloc.nodeId === source.target.nodeId)?.ordinal;
  const rangDestination = post.blocks.find((bloc) => bloc.nodeId === destination.target.nodeId)?.ordinal;
  // ⚠️ « signal absent ≠ signal négatif » : un bloc hors de la fenêtre de
  // relecture n'est pas une preuve d'échec. Seuls DEUX rangs lus contredisent.
  const lisible = rangSource !== undefined && rangDestination !== undefined;
  const ordreTenu = !lisible
    || (input.placement === 'before' ? rangSource < rangDestination : rangSource > rangDestination);

  return buildVerifiedReceipt({
    intent: input.action,
    regimeSuivi: ctx.regimeSuivi,
    preSnapshot: toCanvasSnapshotSummary(pre),
    postSnapshot: toCanvasSnapshotSummary(post),
    resolvedTargets: [targetSummary(source.target, source.candidateCount), targetSummary(destination.target, destination.candidateCount)],
    provider,
    revisionsAvant,
    revisionsApres: idsDeRevision(post.trackedChanges),
    verification: [{
      name: 'ordinal_apres_deplacement',
      passed: ordreTenu,
      message: ordreTenu
        ? lisible
          ? `Relecture : le bloc déplacé est au rang ${String(rangSource)}, la destination au rang `
            + `${String(rangDestination)} — l'ordre demandé (« ${input.placement} ») est tenu.`
          : 'Relecture : au moins un des deux blocs est hors de la fenêtre relue, le déplacement n\'est donc ni '
            + 'prouvé ni infirmé. Ne rejoue pas ce verbe à l\'aveugle : relis le document par canvas_inspect.'
        : `move_range a été annoncé en succès par l'éditeur, mais la relecture rend le bloc déplacé au rang `
          + `${String(rangSource)} et la destination au rang ${String(rangDestination)} : l'ordre demandé `
          + `(« ${input.placement} ») n'est PAS tenu. Le texte d'un bloc déplacé ne change pas — seul son rang le `
          + 'dit, et il dit non. Relis le document par canvas_inspect avant toute autre action.',
    }],
    effects: [{
      // `blocks.move` est déclarée `tracked:true` ; le receipt rabat cette
      // valeur si la relecture ne montre aucune révision neuve (#990).
      kind: 'move_range',
      reviewability: 'tracked',
      description: `Bloc déplacé ${input.placement === 'before' ? 'avant' : 'après'} « ${destination.target.text.slice(0, 60)} ».`,
      nodeIds: [source.target.nodeId],
    }],
  });
}

/** `{canRedo}` de `history.get()`, ou `undefined` si la lecture n'aboutit pas. */
async function peutRetablir(ctx: CanvasActionContext): Promise<boolean | undefined> {
  if (typeof ctx.doc.history?.get !== 'function') return undefined;
  try {
    const etat = await ctx.doc.history.get();
    const canRedo = typeof etat === 'object' && etat !== null ? (etat as { canRedo?: unknown }).canRedo : undefined;
    return typeof canRedo === 'boolean' ? canRedo : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Rétablir ce qu'un `undo_changes` vient d'annuler (#996).
 *
 * Pendant EXACT d'`undo_changes` — `history.redo()`, ni input ni options — avec
 * une chose en plus : **il refuse AVANT d'essayer**. `history.get()` publie
 * `canRedo`, et un `false` net évite de faire brûler un tour à l'agent pour
 * apprendre qu'il n'y a rien à rétablir.
 *
 * ⚠️ **Une lecture qui n'aboutit pas n'est PAS un `false`.** Si `history.get`
 * est absente ou illisible, on tente quand même : refuser sur un défaut de
 * diagnostic transformerait une panne de lecture en panne du verbe.
 */
export async function executeRedoChanges(ctx: CanvasActionContext, input: Extract<TextActionInput, { action: 'redo_changes' }>): Promise<CanvasActionReceipt> {
  const pre = await snapshot(ctx);
  if (typeof ctx.doc.history?.redo !== 'function') {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      code: 'CAPABILITY_UNAVAILABLE',
      message: 'redo_changes exige editor.doc.history.redo (le rétablissement vit sous history, jamais à la racine du '
        + 'Document API — même adresse qu\'undo_changes). Rien n\'a été modifié et relancer échouera à l\'identique.',
    });
  }

  const canRedoAvant = await peutRetablir(ctx);
  if (canRedoAvant === false) {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      code: 'NOTHING_TO_REDO',
      message: 'redo_changes a été REFUSÉ avant toute écriture : l\'éditeur annonce canRedo:false, il n\'y a donc RIEN '
        + 'à rétablir. Le document est intact. Ce verbe n\'est utile qu\'immédiatement après un undo_changes ; sans '
        + 'annulation préalable, il n\'a pas d\'objet.',
      nextAction: 'N\'appelle redo_changes qu\'après un undo_changes. Pour écrire du contenu, utilise un verbe d\'écriture.',
      recovery: {
        action: 'retry',
        reason: 'Rien n\'a été écrit. Ré-inspecter rendrait le même document : il n\'y a aucune annulation à rétablir.',
      },
    });
  }

  const attendue = expectedRevisionFor(ctx, pre.revision);
  // ⚠️ Aucun argument : `history.redo()` ne prend ni input ni options, comme son
  // jumeau. La révision attendue est tout de même calculée et passée à
  // `trackedMutation`, qui l'assortit des trois gardes de #939.
  const provider = await trackedMutation(ctx, attendue, 'redo', () => ctx.doc.history?.redo?.());

  const post = await snapshot(ctx);
  const canRedoApres = await peutRetablir(ctx);
  ctx.verifierEpoque('redo_changes:receipt');

  // Deux preuves, et il faut l'UNE ou l'autre : la pile de rétablissement s'est
  // vidée, ou le document a bougé. Exiger les deux ferait échouer un rétablissement
  // réel sur un document qui portait plusieurs annulations empilées.
  const pileVidee = canRedoApres === false;
  const documentABouge = post.revision !== pre.revision;

  return buildVerifiedReceipt({
    intent: input.action,
    regimeSuivi: ctx.regimeSuivi,
    preSnapshot: toCanvasSnapshotSummary(pre),
    postSnapshot: toCanvasSnapshotSummary(post),
    resolvedTargets: [],
    provider,
    revisionsAvant: idsDeRevision(pre.trackedChanges),
    revisionsApres: idsDeRevision(post.trackedChanges),
    // Rétablir ne POSE pas de révision suivie : sa preuve est le mouvement de la
    // pile et de la révision du document, pas une identité neuve.
    mouvementAttendu: 'aucun',
    verification: [{
      name: 'redo_prouve',
      passed: pileVidee || documentABouge,
      message: pileVidee || documentABouge
        ? `Relecture : ${pileVidee ? 'la pile de rétablissement est vide' : 'la révision du document a changé'} — le `
          + 'rétablissement est prouvé.'
        : 'redo_changes a été annoncé en succès par l\'éditeur, mais ni la révision du document ni la pile de '
          + 'rétablissement n\'ont bougé : rien n\'a été rétabli. Relis le document par canvas_inspect avant toute '
          + 'autre action — ne rejoue pas ce verbe, il échouerait à l\'identique.',
    }],
    effects: [{
      kind: 'redo',
      // Un rétablissement ne crée aucune révision à relire (jumeau d'`undo`).
      reviewability: 'none',
      description: 'Rétabli la dernière modification annulée.',
    }],
  });
}

export const CANVAS_TEXT_ACTION_EXECUTORS = {
  replace_text: executeReplaceText,
  delete_block_range: executeDeleteBlockRange,
  accept_tracked_changes: executeAcceptTrackedChanges,
  reject_tracked_changes: executeRejectTrackedChanges,
  undo_changes: executeUndoChanges,
  redo_changes: executeRedoChanges,
  move_range: executeMoveRange,
} satisfies Record<CanvasTextActionName, (ctx: CanvasActionContext, input: never) => Promise<CanvasActionReceipt>>;
